import React, { useEffect, useState, useMemo } from 'react';
import { ingestionService } from './ingestionService';
import {
  SafetyReport,
  IngestionKpi,
  DynamicFormTemplate,
  IngestionReportCategory,
} from './types';
import { getActiveSessionOrganizationCode } from '../../shared/api';
import { QuestionEditorModal } from './components/QuestionEditorModal';
import { Annex13ReportModal } from './components/Annex13ReportModal';
import { SubmitReportModal } from '../../components/SubmitReportModal';
import { ReportDetailsModal } from '../../components/ReportDetailsModal';
import { taxonomyService } from '../../shared/taxonomyService';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';
import { isRoleContentRestricted } from '../../shared/rolePermissions';

const SAFETY_TYPE_OPTIONS = [
  { value: 'SAFETY', label: 'General Safety Issue (SMS)' },
  { value: 'OCCURRENCE', label: 'Aviation Occurrence (ECCAIRS Mandatory)' },
  { value: 'HAZARD', label: 'Safety Hazard Observation' },
  { value: 'FLIGHT_OPS', label: 'Flight Operations Safety (ASR)' },
  { value: 'GROUND_OPS', label: 'Ramp & Ground Safety (GSR)' },
  { value: 'MAINTENANCE', label: 'Technical / Part-145 Maintenance Safety' },
  { value: 'FATIGUE', label: 'Crew Fatigue Risk Report (FRM)' },
];

const NON_SAFETY_TYPE_OPTIONS = [
  { value: 'QUALITY', label: 'Quality & Process Non-Conformance (QMS)' },
  { value: 'COMPLIANCE', label: 'Regulatory & Policy Compliance Observation' },
  { value: 'SECURITY', label: 'Aviation & Corporate Security (AVSEC)' },
  { value: 'CABIN', label: 'Cabin Service & Passenger Experience' },
  { value: 'GENERAL', label: 'General Operational & Administrative Report' },
];

const TARGET_DEPARTMENTS = [
  'Safety & Quality (SMS Central)',
  'Quality Assurance (QMS)',
  'Compliance Monitoring',
  'Corporate & Aviation Security (AVSEC)',
  'Flight Operations',
  'Ground Handling & Ramp Operations',
  'Technical & Part-145 Maintenance',
  'CAMO / Continuing Airworthiness',
  'Cabin Operations & Service',
  'Training Academy',
  'Administration & Facilities',
];

const VALIDATION_RISK_OPTIONS = [
  'Assign Bow Tie (Risk Management Module)',
  'Risk Matrix (5x5 Hazard Assessment)',
  'Direct Safety Action Notice',
  'None (Low Consequence)',
];

const VALIDATION_CAA_OPTIONS = [
  'CAA Responsible decides',
  'Yes (Mandatory Occurrence Report - 72h MOR)',
  'No (Internal Safety Report only)',
];


const FORM_TITLE_EN: Record<string, string> = {
  'Ucus Operasyon Bildirim Formu (ASR)': 'Flight Operations Occurrence Report (ASR)',
  'Uçuş Operasyon Bildirim Formu (ASR)': 'Flight Operations Occurrence Report (ASR)',
  'Genel Emniyet Bildirim Formu': 'General Safety Occurrence Form (SMS)',
};

const FORM_DESC_EN: Record<string, string> = {
  'Pilot ve kokpit ekibi icin hava araci operasyonel olay raporu.':
    'Aircraft operational incident and occurrence report for pilots and cockpit flight crew.',
  'Pilot ve kokpit ekibi için hava aracı operasyonel olay raporu.':
    'Aircraft operational incident and occurrence report for pilots and cockpit flight crew.',
  'Tum operasyonel emniyet olaylari ve tehlikeleri icin standart bildirim formu.':
    'Standard report form for all operational safety occurrences, hazards, and near-misses.',
  'Tüm operasyonel emniyet olayları ve tehlikeleri için standart bildirim formu.':
    'Standard report form for all operational safety occurrences, hazards, and near-misses.',
};

const QUESTION_LABEL_EN: Record<string, string> = {
  'Ucus Safhasi (Taxi, Takeoff, Cruise, Landing)': 'Flight Phase (Taxi, Takeoff, Cruise, Landing)',
  'Uçuş Safhası (Taxi, Takeoff, Cruise, Landing)': 'Flight Phase (Taxi, Takeoff, Cruise, Landing)',
  'Hava Durumu ve Gorus Sartlari': 'Weather Conditions and Visibility',
  'Hava Durumu ve Görüş Şartları': 'Weather Conditions and Visibility',
  'Olay Aninda Alinan Acil Onlem': 'Immediate Mitigating Action Taken',
  'Olay Anında Alınan Acil Önlem': 'Immediate Mitigating Action Taken',
};

const QUESTION_SECTION_EN: Record<string, string> = {
  'Ucus Verileri': 'Flight Data',
  'Uçuş Verileri': 'Flight Data',
  'Olay Detayi': 'Event Details',
  'Olay Detayı': 'Event Details',
};

export function localizeFormTitle(title?: string): string {
  if (!title) return '';
  return FORM_TITLE_EN[title] || title;
}

export function localizeFormDesc(desc?: string): string {
  if (!desc) return '';
  return FORM_DESC_EN[desc] || desc;
}

export function localizeQuestionLabel(label?: string): string {
  if (!label) return '';
  return QUESTION_LABEL_EN[label] || label;
}

export function localizeSectionName(sec?: string): string {
  if (!sec) return '';
  return QUESTION_SECTION_EN[sec] || sec;
}

export type IngestionTab =
  | 'all'
  | 'statistic'
  | 'waiting_assessment'
  | 'info_only'
  | 'occurrence'
  | 'incident'
  | 'investigation'
  | 'my'
  | 'drafts';

export interface IngestionReportingPageProps {
  onNavigateTab?: (tab: string, extra?: Record<string, string | null>) => void;
}

export const IngestionReportingPage: React.FC<IngestionReportingPageProps> = ({ onNavigateTab }) => {
  const currentUserRole = useMemo(() => {
    try {
      const u = localStorage.getItem('auth_user');
      return u ? JSON.parse(u).user_type : '';
    } catch {
      return '';
    }
  }, []);
  const isRestrictedScope = isRoleContentRestricted(currentUserRole);

  const [reports, setReports] = useState<SafetyReport[]>([]);
  const [kpis, setKpis] = useState<IngestionKpi | null>(null);
  const [forms, setForms] = useState<DynamicFormTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<IngestionTab>('all');
  const [categoryFilter, setCategoryFilter] = useState<'ALL' | 'SAFETY' | 'NON_SAFETY'>('ALL');

  // User session
  const [currentUser] = useState<any>(() => {
    try {
      const saved = localStorage.getItem('auth_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Report Details & Lifecycle Status Inspection Modal state
  const [inspectingReport, setInspectingReport] = useState<SafetyReport | null>(null);
  const [annex13Report, setAnnex13Report] = useState<SafetyReport | null>(null);

  // Filter states for All Reports Tab
  const [allFilterSearch, setAllFilterSearch] = useState<string>('');
  const [allFilterStartDate, setAllFilterStartDate] = useState<string>('');
  const [allFilterEndDate, setAllFilterEndDate] = useState<string>('');
  const [allFilterStatus, setAllFilterStatus] = useState<string>('ALL');
  const [allFilterKind, setAllFilterKind] = useState<string>('ALL');
  const [allFilterSeverity, setAllFilterSeverity] = useState<string>('ALL');

  // Filter states for Info Only Tab
  const [infoFilterSearch, setInfoFilterSearch] = useState<string>('');
  const [infoFilterKind, setInfoFilterKind] = useState<string>('ALL');

  // Filter states for Incident Reports Tab
  const [incidentFilterSearch, setIncidentFilterSearch] = useState<string>('');
  const [incidentFilterDept, setIncidentFilterDept] = useState<string>('ALL');

  // Filter states for Investigation Reports Tab
  const [investigationFilterSearch, setInvestigationFilterSearch] = useState<string>('');
  const [investigationFilterSeverity, setInvestigationFilterSeverity] = useState<string>('ALL');
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [mySubmissionsFilter, setMySubmissionsFilter] = useState<'ALL' | 'SAFETY' | 'NON_SAFETY'>('ALL');

  // Reporter Edit Report Modal state (editable before SMS conducts assessment)
  const [editingReport, setEditingReport] = useState<SafetyReport | null>(null);
  const [editTitle, setEditTitle] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const [editEventDate, setEditEventDate] = useState<string>('');
  const [editLocation, setEditLocation] = useState<string>('');
  const [editFlightNumber, setEditFlightNumber] = useState<string>('');
  const [editAircraftReg, setEditAircraftReg] = useState<string>('');
  const [editSeverity, setEditSeverity] = useState<string>('MEDIUM');
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
  const [editSuccessMsg, setEditSuccessMsg] = useState<string | null>(null);
  const [editErrorMsg, setEditErrorMsg] = useState<string | null>(null);

  const openEditModal = (report: SafetyReport) => {
    setEditingReport(report);
    setEditTitle(report.title || '');
    setEditDescription(report.description || '');
    setEditEventDate(report.event_date ? new Date(report.event_date).toISOString().substring(0, 16) : '');
    setEditLocation(report.event_location || report.airport_code || '');
    setEditFlightNumber(report.flight_number || '');
    setEditAircraftReg(report.aircraft_registration || '');
    setEditSeverity(report.initial_severity || 'MEDIUM');
    setEditSuccessMsg(null);
    setEditErrorMsg(null);
  };

  const handleSaveReportEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingReport) return;
    setIsSavingEdit(true);
    setEditErrorMsg(null);
    try {
      await ingestionService.updateReport(editingReport.id, {
        title: editTitle,
        description: editDescription,
        event_date: editEventDate ? new Date(editEventDate).toISOString() : editingReport.event_date,
        event_location: editLocation,
        airport_code: editLocation ? editLocation.substring(0, 4) : undefined,
        flight_number: editFlightNumber,
        aircraft_registration: editAircraftReg,
        initial_severity: editSeverity as any,
      });
      setEditSuccessMsg('Report updated successfully. Edit activity recorded in audit trail.');
      setTimeout(() => {
        setEditingReport(null);
        setEditSuccessMsg(null);
        loadData();
      }, 1200);
    } catch (err: any) {
      setEditErrorMsg(err?.message || 'Failed to update report');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Wizard Modal state
  // Modern Unified Submit Report Modal (Same as Header Submit Report)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState<boolean>(false);
  const [submitModalCategory, setSubmitModalCategory] = useState<IngestionReportCategory>('SAFETY');
  const [submitModalKindId, setSubmitModalKindId] = useState<string>('PILOT');

  // Legacy Wizard Modal state
  const [showWizardModal, setShowWizardModal] = useState<boolean>(false);
  const [wizardStep, setWizardStep] = useState<number>(1);
  const [reportCategory, setReportCategory] = useState<IngestionReportCategory>('SAFETY');
  const [repType, setRepType] = useState<string>('SAFETY');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [eventDate, setEventDate] = useState<string>(new Date().toISOString().substring(0, 16));
  const [airportCode, setAirportCode] = useState<string>('');
  const [flightNumber, setFlightNumber] = useState<string>('');
  const [aircraftReg, setAircraftReg] = useState<string>('');
  const [eventLocation, setEventLocation] = useState<string>('');
  const [initialSeverity, setInitialSeverity] = useState<string>('MEDIUM');
  const [identityMode, setIdentityMode] = useState<string>('IDENTIFIED');
  const [dynamicAnswers, setDynamicAnswers] = useState<Record<string, any>>({});
  const [otherInputs, setOtherInputs] = useState<Record<string, string>>({});
  const [createdAnonToken, setCreatedAnonToken] = useState<{ report_id: string; tracking_code: string } | null>(null);

  // SafetyManager365 Operational & Meteorological Fields State
  const [depAirport, setDepAirport] = useState<string>('');
  const [destAirport, setDestAirport] = useState<string>('');
  const [depTime, setDepTime] = useState<string>('');
  const [sectorOperated, setSectorOperated] = useState<string>('');
  const [altitudeFL, setAltitudeFL] = useState<string>('');
  const [runwayUsed, setRunwayUsed] = useState<string>('');
  const [flightPhaseState, setFlightPhaseState] = useState<string>('TAKEOFF');
  const [natureOfFlight, setNatureOfFlight] = useState<string>('SCHEDULED_PASSENGER');
  const [isEtops, setIsEtops] = useState<boolean>(false);
  const [oatCelsius, setOatCelsius] = useState<string>('');
  const [cloudHeight, setCloudHeight] = useState<string>('');
  const [visibilityMeters, setVisibilityMeters] = useState<string>('');
  const [daylightCondition, setDaylightCondition] = useState<string>('DAY');
  const [icingCondition, setIcingCondition] = useState<string>('NONE');
  const [turbulenceCondition, setTurbulenceCondition] = useState<string>('NONE');
  const [runwayCondition, setRunwayCondition] = useState<string>('DRY');

  // SafetyManager365 Registrations & Relations State
  const [registeredAircrafts, setRegisteredAircrafts] = useState<any[]>([]);
  const [relateModalReport, setRelateModalReport] = useState<SafetyReport | null>(null);
  const [suggestedMatches, setSuggestedMatches] = useState<SafetyReport[]>([]);
  const [selectedMasterId, setSelectedMasterId] = useState<string>('');
  const [relateComment, setRelateComment] = useState<string>('');
  const [unrelateModalReport, setUnrelateModalReport] = useState<SafetyReport | null>(null);
  const [unrelateComment, setUnrelateComment] = useState<string>('');

  // Cost Analyst Modal State
  const [costModalReport, setCostModalReport] = useState<SafetyReport | null>(null);
  const [directCost, setDirectCost] = useState<string>('');
  const [indirectCost, setIndirectCost] = useState<string>('');
  const [costCurrency, setCostCurrency] = useState<string>('USD');
  const [costNotes, setCostNotes] = useState<string>('');

  // Due Date Filter View
  const [dueDateViewOnly, setDueDateViewOnly] = useState<boolean>(false);

  // Question Editor Modal state for Admin Form Builder
  const [editingQuestion, setEditingQuestion] = useState<{ question: any; formTitle?: string } | null>(null);

  const isAdmin =
    currentUser?.role === 'SYSTEM_ADMIN' ||
    currentUser?.role === 'ORGANIZATION_ADMIN' ||
    currentUser?.roles?.includes('SYSTEM_ADMIN') ||
    currentUser?.roles?.includes('ORGANIZATION_ADMIN') ||
    currentUser?.role === 'SAFETY_MANAGER';

  // SMS Triage & Reclassification Modal state
  const [triageReport, setTriageReport] = useState<SafetyReport | null>(null);
  const [triageCategory, setTriageCategory] = useState<IngestionReportCategory>('SAFETY');
  const [triageType, setTriageType] = useState<string>('SAFETY');
  const [triageSeverity, setTriageSeverity] = useState<string>('MEDIUM');
  const [triageDepartment, setTriageDepartment] = useState<string>('Safety & Quality (SMS Central)');
  const [triageStatus, setTriageStatus] = useState<string>('ASSIGNED');
  const [triageReason, setTriageReason] = useState<string>('');

  // Report Validation Workflow (SCRUM-8) state
  const [reportsInValidation, setReportsInValidation] = useState<SafetyReport[]>([]);
  const [reviewingReport, setReviewingReport] = useState<SafetyReport | null>(null);
  const [reviewCategory, setReviewCategory] = useState<IngestionReportCategory>('SAFETY');
  const [reviewReportType, setReviewReportType] = useState<string>('OCCURRENCE');
  const [reviewIsMor, setReviewIsMor] = useState<boolean>(false);
  const [deletingReport, setDeletingReport] = useState<SafetyReport | null>(null);
  const [deleteReason, setDeleteReason] = useState<string>('');
  const [reviewSideTab, setReviewSideTab] = useState<'assessment' | 'general' | 'text' | 'attachments'>('assessment');
  const [reviewAreaOfOccurrence, setReviewAreaOfOccurrence] = useState<string>('Air Safety Report (ASR)');
  const [reviewTypeOfOccurrence, setReviewTypeOfOccurrence] = useState<string>('ATM/CNS');
  const [reviewEventClassification, setReviewEventClassification] = useState<string>('');
  const [reviewInitialSeverity, setReviewInitialSeverity] = useState<string>('MEDIUM');
  const [reviewCorrectiveActions, setReviewCorrectiveActions] = useState<boolean>(false);
  const [reviewActionDueDate, setReviewActionDueDate] = useState<string>('');
  const [reviewResponsibleDept, setReviewResponsibleDept] = useState<string>('Flight Operations');
  const [reviewValidationNotes, setReviewValidationNotes] = useState<string>('');
  const [validationSaving, setValidationSaving] = useState<boolean>(false);

  // Report Assessment Workflow (Info Only, Corrective Action, Start Investigation)
  const [assessmentOutcome, setAssessmentOutcome] = useState<'INFO_ONLY' | 'CORRECTIVE_ACTION' | 'INVESTIGATION'>('INFO_ONLY');
  const [actionTitle, setActionTitle] = useState<string>('');
  const [actionType, setActionType] = useState<'CORRECTIVE' | 'PREVENTIVE' | 'CONTAINMENT'>('CORRECTIVE');
  const [actionCustomLabel, setActionCustomLabel] = useState<string>('Corrective Action (CAPA)');
  const [actionDepartment, setActionDepartment] = useState<string>('Flight Operations');
  const [actionAssignee, setActionAssignee] = useState<string>('');
  const [actionDueDate, setActionDueDate] = useState<string>('');
  const [actionPriority, setActionPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'IMMEDIATE'>('MEDIUM');
  const [investigationTitle, setInvestigationTitle] = useState<string>('');
  const [investigationType, setInvestigationType] = useState<'FORMAL' | 'COMPREHENSIVE' | 'PRELIMINARY' | 'DESK' | 'SPECIAL'>('FORMAL');
  const [investigationPriority, setInvestigationPriority] = useState<'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'>('MEDIUM');
  const [leadInvestigator, setLeadInvestigator] = useState<string>('');
  const [targetCompletionDate, setTargetCompletionDate] = useState<string>('');
  const [investigationSummary, setInvestigationSummary] = useState<string>('');

  const [taxonomyVersion, setTaxonomyVersion] = useState<number>(0);

  // Subscribe to live taxonomy updates from Organization Settings
  useEffect(() => {
    return taxonomyService.subscribe(() => {
      setTaxonomyVersion((v) => v + 1);
    });
  }, []);

  const taxonomyAreas = useMemo(() => taxonomyService.getAllAreaNames(), [taxonomyVersion]);
  const taxonomyTypes = useMemo(() => {
    if (reviewAreaOfOccurrence) {
      const types = taxonomyService.getTypesForArea(reviewAreaOfOccurrence);
      if (types.length > 0) return types;
    }
    return taxonomyService.getAllTypeNames();
  }, [reviewAreaOfOccurrence, taxonomyVersion]);
  const taxonomyClassifications = useMemo(() => {
    const list = taxonomyService.getClassificationsForAreaAndType(reviewAreaOfOccurrence, reviewTypeOfOccurrence);
    if (list.length > 0) return list;
    return taxonomyService.getAllClassificationNames();
  }, [reviewAreaOfOccurrence, reviewTypeOfOccurrence, taxonomyVersion]);

  // Approve dialog
  const [approvingReport, setApprovingReport] = useState<SafetyReport | null>(null);
  const [approveReportType, setApproveReportType] = useState<string>('Occurrence');
  const [approveRiskAssessmentType, setApproveRiskAssessmentType] = useState<string>('Assign Bow Tie (Risk Management Module)');
  const [approveMandatoryCaa, setApproveMandatoryCaa] = useState<string>('CAA Responsible decides');
  const [approveNotes, setApproveNotes] = useState<string>('');

  // Reject dialog
  const [rejectingReport, setRejectingReport] = useState<SafetyReport | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');

  useEffect(() => {
    loadData();

    // Check if subTab or filter or reportId is present in URL
    const params = new URLSearchParams(window.location.search);
    const subTabFromUrl = params.get('subTab') || params.get('tabView') || params.get('tab');
    if (subTabFromUrl === 'waiting_assessment' || subTabFromUrl === 'validation' || params.get('filter') === 'pending') {
      setActiveTab('waiting_assessment');
      loadValidationReports();
    } else if (subTabFromUrl === 'statistic' || subTabFromUrl === 'kpi') {
      setActiveTab('statistic');
    } else if (subTabFromUrl === 'info_only') {
      setActiveTab('info_only');
    } else if (subTabFromUrl === 'occurrence' || subTabFromUrl === 'occurrences' || subTabFromUrl === 'incident') {
      setActiveTab('occurrence');
    } else if (subTabFromUrl === 'investigation') {
      setActiveTab('investigation');
    } else if (subTabFromUrl === 'my') {
      setActiveTab('my');
    } else if (subTabFromUrl === 'drafts') {
      setActiveTab('drafts');
    } else if (subTabFromUrl === 'all') {
      setActiveTab('all');
    }
    const checkUrlReport = () => {
      const p = new URLSearchParams(window.location.search);
      const reportIdFromUrl = p.get('reportId') || p.get('id') || p.get('reportNumber');
      if (reportIdFromUrl) {
        setLoadingDetails(true);
        ingestionService
          .getReportById(reportIdFromUrl)
          .then((full) => {
            if (full) {
              setInspectingReport(full);
            }
          })
          .catch((err) => {
            console.warn('Could not auto-open report from URL query parameter:', err);
          })
          .finally(() => {
            setLoadingDetails(false);
          });
      }
    };

    checkUrlReport();
    window.addEventListener('popstate', checkUrlReport);
    return () => window.removeEventListener('popstate', checkUrlReport);
  }, []);

  const handleNavigateToOccurrence = (report: SafetyReport) => {
    const target = report.occurrence_number || report.occurrence_id;
    const params = new URLSearchParams();
    params.set('tab', 'occurrences');
    if (target) {
      if (target.startsWith('OCR-')) {
        params.set('occurrenceNumber', target);
      } else {
        params.set('occurrenceId', target);
      }
    }
    const newUrl = `?${params.toString()}`;
    window.history.pushState({ tab: 'occurrences' }, '', newUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleNavigateToInvestigation = (report: SafetyReport) => {
    const target = report.investigation_number || report.investigation_id;
    const params = new URLSearchParams();
    params.set('tab', 'investigations');
    if (target) {
      if (target.startsWith('INV-')) {
        params.set('investigationNumber', target);
      } else {
        params.set('investigationId', target);
      }
    }
    const newUrl = `?${params.toString()}`;
    window.history.pushState({ tab: 'investigations' }, '', newUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleCloseInspectReport = () => {
    setInspectingReport(null);
    const params = new URLSearchParams(window.location.search);
    if (params.has('reportId') || params.has('id')) {
      params.delete('reportId');
      params.delete('id');
      const newQuery = params.toString() ? `?${params.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', newQuery);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [allReports, kpiData, formTemplates, inVal, aircrafts] = await Promise.all([
        ingestionService.getAllReports(),
        ingestionService.getKpis(),
        ingestionService.getFormTemplates(),
        ingestionService.getReportsInValidation().catch(() => []),
        ingestionService.getAircraftRegistrations().catch(() => []),
      ]);
      setRegisteredAircrafts(aircrafts || []);
      setReports(allReports);
      setKpis(kpiData);
      setForms(formTemplates);
      setReportsInValidation(inVal || []);
    } catch (err) {
      console.error('Failed to load reports data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadValidationReports = async () => {
    try {
      const inVal = await ingestionService.getReportsInValidation();
      setReportsInValidation(inVal || []);
    } catch (err) {
      console.error('Failed to load reports in validation:', err);
    }
  };

  const openReviewModal = (report: SafetyReport) => {
    setReviewingReport(report);
    setReviewSideTab('assessment');

    const defaultArea = taxonomyService.getAllAreaNames()[0] || 'Air Safety Report (ASR)';
    const chosenArea = report.area_of_occurrence || defaultArea;
    setReviewAreaOfOccurrence(chosenArea);

    const availableTypes = taxonomyService.getTypesForArea(chosenArea);
    const defaultType = availableTypes[0] || taxonomyService.getAllTypeNames()[0] || '';
    const chosenType = report.type_of_occurrence || defaultType;
    setReviewTypeOfOccurrence(chosenType);

    const availableClasses = taxonomyService.getClassificationsForAreaAndType(chosenArea, chosenType);
    const defaultClass = availableClasses[0] || taxonomyService.getAllClassificationNames()[0] || '';
    setReviewEventClassification(report.event_classification || defaultClass);

    setReviewInitialSeverity(report.initial_severity || 'MEDIUM');
    setReviewCorrectiveActions(report.corrective_actions_required ?? false);
    setReviewActionDueDate(report.action_due_date ? report.action_due_date.substring(0, 10) : '');
    setReviewResponsibleDept(report.responsible_department || report.assigned_department || 'Flight Operations');
    setReviewValidationNotes(report.validation_notes || '');

    // Initialize Assessment Decision parameters
    const initialOutcome = report.assessment_outcome || (report.initial_severity === 'CRITICAL' ? 'INVESTIGATION' : report.corrective_actions_required ? 'CORRECTIVE_ACTION' : 'INFO_ONLY');
    setAssessmentOutcome(initialOutcome);

    setActionTitle(`Corrective Action: [${report.report_number}] ${report.title}`);
    setActionType('CORRECTIVE');
    setActionCustomLabel('Corrective Action (CAPA)');
    setActionDepartment(report.responsible_department || report.assigned_department || 'Flight Operations');
    setActionAssignee('');
    const plus14 = new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString().substring(0, 10);
    setActionDueDate(report.action_due_date ? report.action_due_date.substring(0, 10) : plus14);
    setActionPriority(report.initial_severity === 'CRITICAL' ? 'IMMEDIATE' : report.initial_severity === 'HIGH' ? 'HIGH' : 'MEDIUM');

    setInvestigationTitle(`[${report.report_number}] ${report.title}`);
    setInvestigationType('FORMAL');
    setInvestigationPriority(report.initial_severity === 'CRITICAL' ? 'CRITICAL' : report.initial_severity === 'HIGH' ? 'HIGH' : 'MEDIUM');
    setLeadInvestigator('');
    const plus30 = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().substring(0, 10);
    setTargetCompletionDate(plus30);
    setInvestigationSummary(report.description || report.title);
  };

  const handleExecuteAssessment = async () => {
    if (!reviewingReport) return;
    try {
      setValidationSaving(true);
      const res = await ingestionService.assessReport(reviewingReport.id, {
        assessment_outcome: assessmentOutcome,
        assessment_notes: reviewValidationNotes,
                area_of_occurrence: reviewAreaOfOccurrence,
        type_of_occurrence: reviewTypeOfOccurrence,
        event_classification: reviewEventClassification,
        initial_severity: reviewInitialSeverity,
        // Corrective Action fields
        action_title: actionTitle,
        action_type: actionType,
        action_custom_label: actionCustomLabel,
        responsible_department: actionDepartment,
        assignee_name: actionAssignee,
        action_due_date: actionDueDate,
        action_priority: actionPriority,
        // Investigation fields
        investigation_title: investigationTitle,
        investigation_type: investigationType,
        investigation_priority: investigationPriority,
        lead_investigator_name: leadInvestigator,
        target_completion_date: targetCompletionDate,
        investigation_summary: investigationSummary,
      });

      setReviewingReport(null);
      await loadData();
      await loadValidationReports();

      if (assessmentOutcome === 'INVESTIGATION' && res.investigation) {
        alert(`🔍 Safety Investigation initiated successfully!\n\nCase Number: ${res.investigation.investigationNumber}\nStatus: INITIATED\nLinked Report: ${reviewingReport.report_number}\n\nYou can view and manage this investigation in the Safety Investigations module.`);
      } else if (assessmentOutcome === 'CORRECTIVE_ACTION' && res.action) {
        alert(`⚡ Corrective Action created successfully!\n\nAction Number: ${res.action.actionNumber}\nDepartment: ${res.action.department}\nDue Date: ${res.action.dueDate}`);
      } else {
        alert(`ℹ️ Report #${reviewingReport.report_number} successfully assessed and filed as Info Only.`);
      }
    } catch (err: any) {
      alert(`Assessment failed: ${err.message || 'Unknown error'}`);
    } finally {
      setValidationSaving(false);
    }
  };

  const handleSaveValidationDraft = async () => {
    if (!reviewingReport) return;
    try {
      setValidationSaving(true);
      const updated = await ingestionService.saveValidationDraft(reviewingReport.id, {
        area_of_occurrence: reviewAreaOfOccurrence,
        type_of_occurrence: reviewTypeOfOccurrence,
        event_classification: reviewEventClassification,
        corrective_actions_required: reviewCorrectiveActions,
        action_due_date: reviewActionDueDate ? new Date(reviewActionDueDate).toISOString() : null,
        responsible_department: reviewResponsibleDept,
        validation_notes: reviewValidationNotes,
        initial_severity: reviewInitialSeverity,
      });
      setReviewingReport(updated);
      await loadData();
      alert('Report validation details saved successfully.');
    } catch (err: any) {
      alert(`Failed to save draft: ${err.message || 'Unknown error'}`);
    } finally {
      setValidationSaving(false);
    }
  };

  const openApproveModal = (report: SafetyReport) => {
    setApprovingReport(report);
    setApproveReportType(report.report_type || 'Occurrence');
    setApproveRiskAssessmentType(report.risk_assessment_type || 'Assign Bow Tie (Risk Management Module)');
    setApproveMandatoryCaa(report.mandatory_caa_reporting || 'CAA Responsible decides');
    setApproveNotes(reviewValidationNotes || report.validation_notes || '');
  };

  const handleConfirmApprove = async () => {
    if (!approvingReport) return;
    try {
      setValidationSaving(true);
      await ingestionService.approveValidation(approvingReport.id, {
        report_type: approveReportType,
        area_of_occurrence: reviewAreaOfOccurrence,
        type_of_occurrence: reviewTypeOfOccurrence,
        event_classification: reviewEventClassification,
        initial_severity: reviewInitialSeverity,
        corrective_actions_required: reviewCorrectiveActions,
        action_due_date: reviewActionDueDate ? new Date(reviewActionDueDate).toISOString() : null,
        responsible_department: reviewResponsibleDept,
        validation_notes: approveNotes,
        risk_assessment_type: approveRiskAssessmentType,
        mandatory_caa_reporting: approveMandatoryCaa,
      });
      setApprovingReport(null);
      setReviewingReport(null);
      await loadData();
      alert('Report has been successfully validated and approved!');
    } catch (err: any) {
      alert(`Approval failed: ${err.message || 'Unknown error'}`);
    } finally {
      setValidationSaving(false);
    }
  };

  const openRejectModal = (report: SafetyReport) => {
    setRejectingReport(report);
    setRejectReason('');
  };

  const openDeleteModal = (report: SafetyReport) => {
    setDeletingReport(report);
    setDeleteReason('');
  };

  const handleConfirmDelete = async () => {
    if (!deletingReport) return;
    const cleanReason = (deleteReason || '').trim();
    if (!cleanReason || cleanReason.length < 3) {
      alert('A valid reason for deletion (minimum 3 characters) is mandatory.');
      return;
    }
    try {
      setValidationSaving(true);
      await ingestionService.deleteReportWithReason(deletingReport.id, cleanReason);
      setDeletingReport(null);
      if (reviewingReport?.id === deletingReport.id) {
        setReviewingReport(null);
      }
      await loadData();
      await loadValidationReports();
      alert(`Report #${deletingReport.report_number} was soft-deleted and permanently recorded in the audit trail.`);
    } catch (err: any) {
      alert(`Deletion failed: ${err.message || 'Unknown error'}`);
    } finally {
      setValidationSaving(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectingReport) return;
    if (!rejectReason.trim()) {
      alert('Please enter a rejection reason.');
      return;
    }
    try {
      setValidationSaving(true);
      await ingestionService.rejectValidation(rejectingReport.id, {
        reason: rejectReason.trim(),
      });
      setRejectingReport(null);
      setReviewingReport(null);
      await loadData();
      alert('Report has been rejected.');
    } catch (err: any) {
      alert(`Rejection failed: ${err.message || 'Unknown error'}`);
    } finally {
      setValidationSaving(false);
    }
  };

  const handleInspectReport = async (report: SafetyReport) => {
    try {
      setLoadingDetails(true);
      const full = await ingestionService.getReportById(report.id);
      setInspectingReport(full || report);
    } catch {
      setInspectingReport(report);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCategorySwitch = (cat: IngestionReportCategory) => {
    setReportCategory(cat);
    const defaultType = cat === 'NON_SAFETY' ? 'QUALITY' : 'SAFETY';
    setRepType(defaultType);
    setDynamicAnswers({});
  };

  const openWizardForCategory = (cat: IngestionReportCategory) => {
    setSubmitModalCategory(cat);
    setSubmitModalKindId(cat === 'NON_SAFETY' ? 'FACILITIES' : 'PILOT');
    setIsSubmitModalOpen(true);
  };

  const resetWizard = (cat: IngestionReportCategory = 'SAFETY') => {
    setWizardStep(1);
    setReportCategory(cat);
    setRepType(cat === 'NON_SAFETY' ? 'QUALITY' : 'SAFETY');
    setTitle('');
    setDescription('');
    setAirportCode('');
    setFlightNumber('');
    setAircraftReg('');
    setEventLocation('');
    setInitialSeverity('MEDIUM');
    setIdentityMode('IDENTIFIED');
    setDynamicAnswers({});
    setOtherInputs({});
    setCreatedAnonToken(null);
  };

  const selectedFormTemplate = forms.find((f) => f.report_type === repType);
  const activeDynamicQuestions = selectedFormTemplate?.current_version?.questions || [];

  const handleCreateReport = async (e: React.FormEvent, asDraft: boolean = false) => {
    e.preventDefault();
    try {
      const payload: any = {
        report_category: reportCategory,
        report_type: repType,
        title,
        description,
        event_date: new Date(eventDate).toISOString(),
        event_location: eventLocation || undefined,
        airport_code: airportCode || undefined,
        flight_number: flightNumber || undefined,
        aircraft_registration: aircraftReg || undefined,
        initial_severity: initialSeverity,
        identity_mode: identityMode,
        is_draft: asDraft,
        form_version_id: selectedFormTemplate?.current_version?.id,
        context_data: {
          ...dynamicAnswers,
          reporter_selected_category: reportCategory,
          reporter_selected_type: repType,
        },
        organization_id: getActiveSessionOrganizationCode() || undefined,
        created_by: identityMode === 'ANONYMOUS' ? undefined : (currentUser?.id || currentUser?.username || 'USR-CURRENT'),
      };

      if (identityMode !== 'ANONYMOUS') {
        const finalName = currentUser?.display_name || currentUser?.full_name || currentUser?.username || 'Authenticated Personnel';
        const finalEmail = currentUser?.primary_email || currentUser?.email || '';
        payload.reporter_name = finalName;
        payload.reporter_email = finalEmail;
        payload.reporter = {
          full_name: finalName,
          email: finalEmail,
          job_title: currentUser?.user_type || currentUser?.role || undefined,
          department: currentUser?.organization?.name || currentUser?.department || undefined,
        };
      }

      const res = await ingestionService.createReport(payload);

      if (res.tracking_code && !asDraft) {
        setCreatedAnonToken({
          report_id: res.report.id,
          tracking_code: res.tracking_code,
        });
      } else {
        setShowWizardModal(false);
        resetWizard(reportCategory);
      }
      await loadData();
    } catch (err: any) {
      alert(`Report submission failed: ${err.message}`);
    }
  };

  const handleTriageTransition = async (reportId: string, targetStatus: string) => {
    try {
      await ingestionService.updateReportStatus(reportId, targetStatus, 'Triage assessment verified');
      await loadData();
    } catch (err: any) {
      alert(`Status update failed: ${err.message}`);
    }
  };

  const openSmsTriageModal = (report: SafetyReport) => {
    const cat: IngestionReportCategory =
      report.report_category === 'NON_SAFETY' ? 'NON_SAFETY' : 'SAFETY';
    setTriageReport(report);
    setTriageCategory(cat);
    setTriageType(report.report_type);
    setTriageSeverity(report.initial_severity || 'MEDIUM');
    const defaultDept =
      report.assigned_department ||
      (cat === 'NON_SAFETY'
        ? report.report_type === 'COMPLIANCE'
          ? 'Compliance Monitoring'
          : report.report_type === 'SECURITY'
          ? 'Corporate & Aviation Security (AVSEC)'
          : report.report_type === 'CABIN'
          ? 'Cabin Operations & Service'
          : 'Quality Assurance (QMS)'
        : 'Safety & Quality (SMS Central)');
    setTriageDepartment(defaultDept);
    setTriageStatus(report.status === 'SUBMITTED' ? 'ASSIGNED' : report.status);
    setTriageReason('');
  };

  const handleSmsTriageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!triageReport) return;
    try {
      await ingestionService.triageAndReclassifyReport(triageReport.id, {
        report_category: triageCategory,
        report_type: triageType,
        initial_severity: triageSeverity,
        assigned_department: triageDepartment,
        target_status: triageStatus,
        reason:
          triageReason ||
          `SMS Triage accepted (${triageCategory} / ${triageType}) and routed to ${triageDepartment}`,
      });
      setTriageReport(null);
      await loadData();
    } catch (err: any) {
      alert(`SMS Triage update failed: ${err.message}`);
    }
  };

  const handleDeleteDraft = async (reportId: string) => {
    if (!confirm('Are you sure you want to delete this draft permanently?')) return;
    try {
      await ingestionService.deleteDraft(reportId);
      await loadData();
    } catch (err: any) {
      alert(`Delete draft failed: ${err.message}`);
    }
  };

  // --- Datasets for 8 Tabs ---
  // 1. Assessed reports (status is not SUBMITTED and not DRAFT)
  const assessedReports = useMemo(() => {
    return reports.filter((r) => r.status !== 'SUBMITTED' && r.status !== 'DRAFT');
  }, [reports]);

  const filteredAssessedReports = useMemo(() => {
    return assessedReports.filter((r) => {
      if (allFilterSearch.trim()) {
        const q = allFilterSearch.toLowerCase();
        const outcomeLabel =
          r.assessment_outcome === 'INVESTIGATION' || r.investigation_number || r.investigation_id
            ? 'investigation'
            : (r.assessment_outcome === 'CORRECTIVE_ACTION' || r.assessment_outcome === 'OCCURRENCE' || r.corrective_action_number || r.occurrence_number || r.corrective_action_id)
              ? 'occurrence corrective action'
              : 'info only';
        const match =
          r.report_number.toLowerCase().includes(q) ||
          r.title.toLowerCase().includes(q) ||
          (r.description && r.description.toLowerCase().includes(q)) ||
          (r.flight_number && r.flight_number.toLowerCase().includes(q)) ||
          (r.aircraft_registration && r.aircraft_registration.toLowerCase().includes(q)) ||
          (r.airport_code && r.airport_code.toLowerCase().includes(q)) ||
          (r.event_location && r.event_location.toLowerCase().includes(q)) ||
          (r.investigation_number && r.investigation_number.toLowerCase().includes(q)) ||
          (r.corrective_action_number && r.corrective_action_number.toLowerCase().includes(q)) ||
          (r.occurrence_number && r.occurrence_number.toLowerCase().includes(q)) ||
          outcomeLabel.includes(q);
        if (!match) return false;
      }
      if (allFilterStartDate) {
        const d = r.event_date ? new Date(r.event_date) : (r.created_at ? new Date(r.created_at) : null);
        if (d && d < new Date(allFilterStartDate)) return false;
      }
      if (allFilterEndDate) {
        const d = r.event_date ? new Date(r.event_date) : (r.created_at ? new Date(r.created_at) : null);
        const end = new Date(allFilterEndDate);
        end.setHours(23, 59, 59, 999);
        if (d && d > end) return false;
      }
      if (allFilterStatus !== 'ALL') {
        if (r.status !== allFilterStatus) return false;
      }
      if (allFilterKind !== 'ALL') {
        const repType = (r.report_type || '').toUpperCase();
        const repCat = (r.report_category || '').toUpperCase();
        if (allFilterKind === 'SAFETY' || allFilterKind === 'NON_SAFETY') {
          if (repCat !== allFilterKind) return false;
        } else {
          if (!repType.includes(allFilterKind) && !repCat.includes(allFilterKind)) return false;
        }
      }
      if (allFilterSeverity !== 'ALL') {
        if ((r.initial_severity || '').toUpperCase() !== allFilterSeverity) return false;
      }
      return true;
    });
  }, [assessedReports, allFilterSearch, allFilterStartDate, allFilterEndDate, allFilterStatus, allFilterKind, allFilterSeverity]);

  // 3. Waiting Assessment reports (SUBMITTED status)
  const waitingAssessmentReports = useMemo(() => {
    const map = new Map<string, SafetyReport>();
    reportsInValidation.forEach((r) => map.set(r.id, r));
    reports.filter((r) => r.status === 'SUBMITTED').forEach((r) => map.set(r.id, r));
    return Array.from(map.values());
  }, [reports, reportsInValidation]);

  // 4. Info Only reports
  const infoOnlyReports = useMemo(() => {
    return reports.filter((r) =>
      r.status !== 'DRAFT' &&
      r.status !== 'SUBMITTED' &&
      (r.assessment_outcome === 'INFO_ONLY' || (r.status === 'CLOSED' && !r.investigation_id && !r.corrective_action_id))
    );
  }, [reports]);

  const filteredInfoOnlyReports = useMemo(() => {
    return infoOnlyReports.filter((r) => {
      if (infoFilterSearch.trim()) {
        const q = infoFilterSearch.toLowerCase();
        const match =
          r.report_number.toLowerCase().includes(q) ||
          r.title.toLowerCase().includes(q) ||
          (r.description && r.description.toLowerCase().includes(q));
        if (!match) return false;
      }
      if (infoFilterKind !== 'ALL') {
        const repType = (r.report_type || '').toUpperCase();
        if (!repType.includes(infoFilterKind)) return false;
      }
      return true;
    });
  }, [infoOnlyReports, infoFilterSearch, infoFilterKind]);

  // 5. Occurrence Reports (Corrective Action required, non-serious)
  const incidentReports = useMemo(() => {
    return reports.filter((r) =>
      r.status !== 'DRAFT' &&
      r.status !== 'SUBMITTED' &&
      (r.assessment_outcome === 'CORRECTIVE_ACTION' ||
        r.assessment_outcome === 'OCCURRENCE' ||
        Boolean(r.corrective_action_id) ||
        Boolean(r.corrective_action_number) ||
        (r.status === 'PENDING_ACTION' && !r.investigation_id))
    );
  }, [reports]);

  const filteredIncidentReports = useMemo(() => {
    return incidentReports.filter((r) => {
      if (incidentFilterSearch.trim()) {
        const q = incidentFilterSearch.toLowerCase();
        const match =
          r.report_number.toLowerCase().includes(q) ||
          r.title.toLowerCase().includes(q) ||
          (r.description && r.description.toLowerCase().includes(q)) ||
          (r.corrective_action_number && r.corrective_action_number.toLowerCase().includes(q));
        if (!match) return false;
      }
      if (incidentFilterDept !== 'ALL') {
        const dept = (r.responsible_department || r.assigned_department || '').toLowerCase();
        if (!dept.includes(incidentFilterDept.toLowerCase())) return false;
      }
      return true;
    });
  }, [incidentReports, incidentFilterSearch, incidentFilterDept]);

  // 6. Investigation Reports (Serious Incident or Accident requiring Annex 13)
  const investigationReports = useMemo(() => {
    return reports.filter((r) =>
      r.status !== 'DRAFT' &&
      r.status !== 'SUBMITTED' &&
      (r.assessment_outcome === 'INVESTIGATION' ||
        Boolean(r.investigation_id) ||
        Boolean(r.investigation_number) ||
        r.status === 'IN_PROGRESS')
    );
  }, [reports]);

  const filteredInvestigationReports = useMemo(() => {
    return investigationReports.filter((r) => {
      if (investigationFilterSearch.trim()) {
        const q = investigationFilterSearch.toLowerCase();
        const match =
          r.report_number.toLowerCase().includes(q) ||
          r.title.toLowerCase().includes(q) ||
          (r.description && r.description.toLowerCase().includes(q)) ||
          (r.investigation_number && r.investigation_number.toLowerCase().includes(q));
        if (!match) return false;
      }
      if (investigationFilterSeverity !== 'ALL') {
        if ((r.initial_severity || '').toUpperCase() !== investigationFilterSeverity) return false;
      }
      return true;
    });
  }, [investigationReports, investigationFilterSearch, investigationFilterSeverity]);

  // 7. My Submissions
  const nonDraftReports = useMemo(() => reports.filter((r) => r.status !== 'DRAFT'), [reports]);
  const userSubmissions = useMemo(() => {
    return nonDraftReports.filter((r) => {
      if (r.identity_mode === 'ANONYMOUS') return false;
      if (!currentUser) return true;
      return (
        r.created_by === currentUser.id ||
        r.created_by === currentUser.username ||
        (currentUser.primary_email && r.reporter_identity?.email === currentUser.primary_email) ||
        (currentUser.display_name && r.reporter_identity?.full_name === currentUser.display_name)
      );
    });
  }, [nonDraftReports, currentUser]);

  const isUsingUserDirect = userSubmissions.length > 0;
  const myBaseList = isUsingUserDirect ? userSubmissions : nonDraftReports;
  const filteredMyReports = useMemo(() => {
    return myBaseList.filter((r) => {
      if (mySubmissionsFilter === 'ALL') return true;
      return (r.report_category || 'SAFETY') === mySubmissionsFilter;
    });
  }, [myBaseList, mySubmissionsFilter]);

  // 8. Drafts
  const draftReportsList = useMemo(() => reports.filter((r) => r.status === 'DRAFT'), [reports]);

  // Sorting instances for reports (defaults to created_at/event_date desc)
  const {
    sortedItems: sortedAssessedReports,
    sortField: assessedSortField,
    sortDirection: assessedSortDirection,
    requestSort: requestAssessedSort,
  } = useTableSort(filteredAssessedReports);

  const {
    sortedItems: sortedWaitingReports,
    sortField: waitingSortField,
    sortDirection: waitingSortDirection,
    requestSort: requestWaitingSort,
  } = useTableSort(waitingAssessmentReports);

  const {
    sortedItems: sortedInfoOnlyReports,
    sortField: infoOnlySortField,
    sortDirection: infoOnlySortDirection,
    requestSort: requestInfoOnlySort,
  } = useTableSort(filteredInfoOnlyReports);

  const {
    sortedItems: sortedIncidentReports,
    sortField: incidentSortField,
    sortDirection: incidentSortDirection,
    requestSort: requestIncidentSort,
  } = useTableSort(filteredIncidentReports);

  const {
    sortedItems: sortedInvestigationReports,
    sortField: investigationSortField,
    sortDirection: investigationSortDirection,
    requestSort: requestInvestigationSort,
  } = useTableSort(filteredInvestigationReports);

  // Pagination instances
  const {
    currentPage: allReportsPage,
    setCurrentPage: setAllReportsPage,
    pageSize: allReportsPageSize,
    setPageSize: setAllReportsPageSize,
    totalItems: totalAllReportsItems,
    paginatedItems: paginatedAssessedReports,
  } = useTablePagination(sortedAssessedReports, 20);

  const {
    currentPage: waitingPage,
    setCurrentPage: setWaitingPage,
    pageSize: waitingPageSize,
    setPageSize: setWaitingPageSize,
    totalItems: totalWaitingItems,
    paginatedItems: paginatedWaitingReports,
  } = useTablePagination(sortedWaitingReports, 20);

  const {
    currentPage: infoOnlyPage,
    setCurrentPage: setInfoOnlyPage,
    pageSize: infoOnlyPageSize,
    setPageSize: setInfoOnlyPageSize,
    totalItems: totalInfoOnlyItems,
    paginatedItems: paginatedInfoOnlyReports,
  } = useTablePagination(sortedInfoOnlyReports, 20);

  const {
    currentPage: incidentPage,
    setCurrentPage: setIncidentPage,
    pageSize: incidentPageSize,
    setPageSize: setIncidentPageSize,
    totalItems: totalIncidentItems,
    paginatedItems: paginatedIncidentReports,
  } = useTablePagination(sortedIncidentReports, 20);

  const {
    currentPage: investigationPage,
    setCurrentPage: setInvestigationPage,
    pageSize: investigationPageSize,
    setPageSize: setInvestigationPageSize,
    totalItems: totalInvestigationItems,
    paginatedItems: paginatedInvestigationReports,
  } = useTablePagination(sortedInvestigationReports, 20);

  const {
    currentPage: mySubmissionsPage,
    setCurrentPage: setMySubmissionsPage,
    pageSize: mySubmissionsPageSize,
    setPageSize: setMySubmissionsPageSize,
    totalItems: totalMySubmissionsItems,
    paginatedItems: paginatedMySubmissions,
  } = useTablePagination(filteredMyReports, 20);

  return (
    <div>
      {isRestrictedScope && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          marginBottom: '16px',
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          color: '#1e40af',
          fontSize: '13px',
          lineHeight: '1.4'
        }}>
          <span style={{ fontSize: '18px' }}>🔒</span>
          <div>
            <strong>Personal Record Scope:</strong> Operational crew view is active ({currentUserRole || 'Frontline'}). In accordance with safety data governance rules, you can view reports authored by you, directly assigned to you, or where you participate in linked investigation tasks.
          </div>
        </div>
      )}

      {/* 8 Sequential Navigation Tabs */}
      <div className="nav-tabs" style={{ flexWrap: 'wrap', gap: '4px' }}>
        {/* 1. All Reports */}
        <button
          onClick={() => setActiveTab('all')}
          className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
        >
          🗂️ All Reports ({assessedReports.length})
        </button>

        {/* 2. Statistic */}
        <button
          onClick={() => setActiveTab('statistic')}
          className={`tab-btn ${activeTab === 'statistic' ? 'active' : ''}`}
        >
          📊 Statistic
        </button>

        {/* 3. Waiting Assesment */}
        <button
          onClick={() => { setActiveTab('waiting_assessment'); loadValidationReports(); }}
          className={`tab-btn ${activeTab === 'waiting_assessment' ? 'active' : ''}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <span>⏳ Waiting Assesment</span>
          {waitingAssessmentReports.length > 0 && (
            <span
              style={{
                background: '#ef4444',
                color: '#ffffff',
                fontSize: '11px',
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: '999px',
                lineHeight: 1,
              }}
            >
              {waitingAssessmentReports.length}
            </span>
          )}
        </button>

        {/* 4. Info Only Reports */}
        <button
          onClick={() => setActiveTab('info_only')}
          className={`tab-btn ${activeTab === 'info_only' ? 'active' : ''}`}
        >
          📄 Info Only Reports ({infoOnlyReports.length})
        </button>

        {/* 5. Occurrence Reports */}
        <button
          onClick={() => setActiveTab('occurrence')}
          className={`tab-btn ${activeTab === 'occurrence' || activeTab === 'incident' ? 'active' : ''}`}
        >
          ⚡ Occurrence Reports ({incidentReports.length})
        </button>

        {/* 6. Investigation Reports */}
        <button
          onClick={() => setActiveTab('investigation')}
          className={`tab-btn ${activeTab === 'investigation' ? 'active' : ''}`}
        >
          🔍 Investigation Reports ({investigationReports.length})
        </button>

        {/* 7. My Submissions */}
        <button
          onClick={() => setActiveTab('my')}
          className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`}
        >
          👤 My Submissions
        </button>

        {/* 8. Drafts */}
        <button
          onClick={() => setActiveTab('drafts')}
          className={`tab-btn ${activeTab === 'drafts' ? 'active' : ''}`}
        >
          📝 Drafts ({draftReportsList.length})
        </button>
      </div>

      {/* --- TAB CONTENT --- */}

      {/* TAB 1: All Reports (Assessed Reports) */}
      {activeTab === 'all' && (
        <div>
          {/* Top Filters Bar */}
          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                  Filter Assessed Reports ({filteredAssessedReports.length} of {assessedReports.length})
                </h4>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                  All safety and operational reports that have completed SMS assessment.
                </p>
              </div>
              {(allFilterSearch || allFilterStartDate || allFilterEndDate || allFilterStatus !== 'ALL' || allFilterKind !== 'ALL' || allFilterSeverity !== 'ALL') && (
                <button
                  type="button"
                  onClick={() => {
                    setAllFilterSearch('');
                    setAllFilterStartDate('');
                    setAllFilterEndDate('');
                    setAllFilterStatus('ALL');
                    setAllFilterKind('ALL');
                    setAllFilterSeverity('ALL');
                  }}
                  className="btn-secondary"
                  style={{ width: 'auto', padding: '4px 10px', fontSize: '11px', color: '#dc2626', borderColor: '#fca5a5' }}
                >
                  ✕ Reset Filters
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                  Search Query
                </label>
                <input
                  type="text"
                  value={allFilterSearch}
                  onChange={(e) => setAllFilterSearch(e.target.value)}
                  placeholder="Report #, flight, title..."
                  style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                  From Date
                </label>
                <input
                  type="date"
                  value={allFilterStartDate}
                  onChange={(e) => setAllFilterStartDate(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                  To Date
                </label>
                <input
                  type="date"
                  value={allFilterEndDate}
                  onChange={(e) => setAllFilterEndDate(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                  Status
                </label>
                <select
                  value={allFilterStatus}
                  onChange={(e) => setAllFilterStatus(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                >
                  <option value="ALL">All Assessed Statuses</option>
                  <option value="CLOSED">CLOSED (Info Only / Completed)</option>
                  <option value="PENDING_ACTION">PENDING_ACTION (Corrective Action)</option>
                  <option value="IN_PROGRESS">IN_PROGRESS (Investigation)</option>
                  <option value="ASSIGNED">ASSIGNED (Forwarded to Dept)</option>
                  <option value="VALIDATED">VALIDATED</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                  Kind of Report
                </label>
                <select
                  value={allFilterKind}
                  onChange={(e) => setAllFilterKind(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                >
                  <option value="ALL">All Kinds of Reports</option>
                  <option value="PILOT">Pilot (ASR - Flight Ops)</option>
                  <option value="CABIN_SAFETY">Cabin Safety (CSR)</option>
                  <option value="MAINTENANCE">Technical / Part-145</option>
                  <option value="GROUND_OPS">Ramp & Ground (GSR)</option>
                  <option value="SECURITY">Aviation Security (AVSEC)</option>
                  <option value="BIRD_STRIKE">Wildlife / Bird Strike</option>
                  <option value="FATIGUE">Crew Fatigue (FRM)</option>
                  <option value="FDP_EXTENSION">FDP Discretion</option>
                  <option value="FACILITIES">Facilities & Infrastructure</option>
                  <option value="SAFETY">General Safety (SMS)</option>
                  <option value="NON_SAFETY">Quality & Compliance (QMS)</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                  Severity
                </label>
                <select
                  value={allFilterSeverity}
                  onChange={(e) => setAllFilterSeverity(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                >
                  <option value="ALL">All Severities</option>
                  <option value="CRITICAL">CRITICAL</option>
                  <option value="HIGH">HIGH</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="LOW">LOW</option>
                </select>
              </div>
            </div>
          </div>

          {/* Assessed Reports Table */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh field="event_date" sortField={assessedSortField} sortDirection={assessedSortDirection} onSort={requestAssessedSort} style={{ width: '120px' }}>
                    Date
                  </SortableTh>
                  <SortableTh field="report_number" sortField={assessedSortField} sortDirection={assessedSortDirection} onSort={requestAssessedSort} style={{ width: '140px' }}>
                    Report ID
                  </SortableTh>
                  <SortableTh field="report_category" sortField={assessedSortField} sortDirection={assessedSortDirection} onSort={requestAssessedSort} style={{ width: '150px' }}>
                    Kind of Report
                  </SortableTh>
                  <SortableTh field="title" sortField={assessedSortField} sortDirection={assessedSortDirection} onSort={requestAssessedSort}>
                    Title & Description
                  </SortableTh>
                  <SortableTh field="assessment_outcome" sortField={assessedSortField} sortDirection={assessedSortDirection} onSort={requestAssessedSort} style={{ width: '160px' }}>
                    Assessment Outcome
                  </SortableTh>
                  <SortableTh field="initial_severity" sortField={assessedSortField} sortDirection={assessedSortDirection} onSort={requestAssessedSort} style={{ width: '110px' }}>
                    Severity
                  </SortableTh>
                  <SortableTh field="status" sortField={assessedSortField} sortDirection={assessedSortDirection} onSort={requestAssessedSort} style={{ width: '130px' }}>
                    Status
                  </SortableTh>
                  <th style={{ width: '100px', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssessedReports.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>🗂️</div>
                      <div style={{ fontWeight: 600, color: '#475569' }}>No assessed reports match your filters.</div>
                      <div style={{ fontSize: '12px', marginTop: '4px' }}>Try clearing or adjusting the filter criteria above.</div>
                    </td>
                  </tr>
                ) : (
                  paginatedAssessedReports.map((r) => {
                    return (
                      <tr key={r.id}>
                        <td style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                          {r.event_date ? new Date(r.event_date).toLocaleDateString() : (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—')}
                        </td>
                        <td
                          style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb', cursor: 'pointer' }}
                          onClick={() => handleInspectReport(r)}
                          title="Click to inspect report details"
                        >
                          {r.report_number}
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              fontWeight: 600,
                              fontSize: '11px',
                            }}
                          >
                            {r.report_type || 'OCCURRENCE'}
                          </span>
                        </td>
                        <td
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleInspectReport(r)}
                          title="Click to inspect report details"
                        >
                          <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '2px' }}>
                            {r.title}
                          </div>
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#64748b',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '360px',
                            }}
                          >
                            {r.description}
                          </div>
                        </td>
                        <td>
                          {r.assessment_outcome === 'INVESTIGATION' || r.investigation_number || r.investigation_id ? (
                            <span
                              className="badge-pill"
                              style={{
                                background: '#f3e8ff',
                                color: '#7e22ce',
                                fontWeight: 700,
                                fontSize: '11px',
                                border: '1px solid #d8b4fe',
                              }}
                              title={r.investigation_number ? `Investigation Case: ${r.investigation_number}` : 'Investigation'}
                            >
                              🔍 Investigation
                            </span>
                          ) : (r.assessment_outcome === 'CORRECTIVE_ACTION' || r.assessment_outcome === 'OCCURRENCE' || r.corrective_action_number || r.occurrence_number || r.corrective_action_id) ? (
                            <span
                              className="badge-pill"
                              style={{
                                background: '#fef3c7',
                                color: '#b45309',
                                fontWeight: 700,
                                fontSize: '11px',
                                border: '1px solid #fde68a',
                              }}
                              title={r.occurrence_number ? `Occurrence: ${r.occurrence_number}` : r.corrective_action_number ? `Action: ${r.corrective_action_number}` : 'Occurrence'}
                            >
                              ⚡ Occurrence
                            </span>
                          ) : (
                            <span
                              className="badge-pill"
                              style={{
                                background: '#ecfdf5',
                                color: '#065f46',
                                fontWeight: 600,
                                fontSize: '11px',
                              }}
                              title={r.info_reference_number ? `Info Ref: ${r.info_reference_number}` : 'Info Only'}
                            >
                              ✓ Info Only
                            </span>
                          )}
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              background:
                                r.initial_severity === 'CRITICAL'
                                  ? '#fee2e2'
                                  : r.initial_severity === 'HIGH'
                                  ? '#ffedd5'
                                  : r.initial_severity === 'MEDIUM'
                                  ? '#eff6ff'
                                  : '#f1f5f9',
                              color:
                                r.initial_severity === 'CRITICAL'
                                  ? '#b91c1c'
                                  : r.initial_severity === 'HIGH'
                                  ? '#c2410c'
                                  : r.initial_severity === 'MEDIUM'
                                  ? '#1d4ed8'
                                  : '#475569',
                            }}
                          >
                            {r.initial_severity || 'MEDIUM'}
                          </span>
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              background: r.status === 'CLOSED' ? '#f0fdf4' : '#f8fafc',
                              color: r.status === 'CLOSED' ? '#166534' : '#334155',
                              border: '1px solid #e2e8f0',
                            }}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => handleNavigateToOccurrence(r)}
                              style={{
                                background: '#fffbeb',
                                color: '#b45309',
                                border: '1px solid #fde68a',
                                padding: '4px 10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                              title="Occurrences modülünde bu olayı aç"
                            >
                              ⚡ Occurrence ↗
                            </button>
                            <button
                              type="button"
                              onClick={() => handleInspectReport(r)}
                              className="btn-secondary"
                              style={{ width: 'auto', padding: '4px 10px', fontSize: '12px' }}
                            >
                              Inspect
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={allReportsPage}
            totalItems={totalAllReportsItems}
            pageSize={allReportsPageSize}
            onPageChange={setAllReportsPage}
            onPageSizeChange={setAllReportsPageSize}
            itemLabel="assessed reports"
          />
        </div>
      )}

      {/* TAB 2: Statistic */}
      {activeTab === 'statistic' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Top Metric Cards */}
          <div className="stats-grid">
            <div
              className="stat-card"
              onClick={() => setActiveTab('all')}
              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              title="Click to view all assessed reports"
            >
              <span className="stat-label">Assessed Reports</span>
              <span className="stat-value">{assessedReports.length}</span>
              <span className="stat-sub">Completed SMS Assessment ✓</span>
            </div>

            <div
              className="stat-card"
              onClick={() => { setActiveTab('waiting_assessment'); loadValidationReports(); }}
              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              title="Click to assess submitted reports"
            >
              <span className="stat-label">Waiting Assessment</span>
              <span className="stat-value" style={{ color: '#d97706' }}>{waitingAssessmentReports.length}</span>
              <span className="stat-sub">Awaiting SMS Assessment & Routing →</span>
            </div>

            <div
              className="stat-card"
              onClick={() => setActiveTab('info_only')}
              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              title="Click to view info only reports"
            >
              <span className="stat-label">Info Only Reports</span>
              <span className="stat-value" style={{ color: '#059669' }}>{infoOnlyReports.length}</span>
              <span className="stat-sub">Logged for Statistical Monitoring</span>
            </div>

            <div
              className="stat-card"
              onClick={() => setActiveTab('occurrence')}
              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              title="Click to view occurrence reports"
            >
              <span className="stat-label">Occurrence Reports</span>
              <span className="stat-value" style={{ color: '#b45309' }}>{incidentReports.length}</span>
              <span className="stat-sub">Corrective Action (CAPA) Required</span>
            </div>

            <div
              className="stat-card"
              onClick={() => setActiveTab('investigation')}
              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              title="Click to view investigation reports"
            >
              <span className="stat-label">Investigation Reports</span>
              <span className="stat-value" style={{ color: '#7e22ce' }}>{investigationReports.length}</span>
              <span className="stat-sub">Serious Incident / Annex 13</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Mandatory (72h MOR)</span>
              <span className="stat-value" style={{ color: '#dc2626' }}>
                {reports.filter((r) => r.is_mandatory_occurrence).length}
              </span>
              <span className="stat-sub">Statutory Aviation Compliance</span>
            </div>
          </div>

          {/* Assessment Pathway Breakdown */}
          <div className="card" style={{ marginBottom: 0 }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '14px' }}>
              📊 Assessment Outcome Distribution
            </h3>
            {(() => {
              const totalNonDraft = reports.filter((r) => r.status !== 'DRAFT').length || 1;
              const pInfo = Math.round((infoOnlyReports.length / totalNonDraft) * 100);
              const pInc = Math.round((incidentReports.length / totalNonDraft) * 100);
              const pInv = Math.round((investigationReports.length / totalNonDraft) * 100);
              const pWait = Math.round((waitingAssessmentReports.length / totalNonDraft) * 100);

              return (
                <div>
                  <div style={{ height: '14px', width: '100%', backgroundColor: '#e2e8f0', borderRadius: '7px', overflow: 'hidden', display: 'flex', marginBottom: '14px' }}>
                    <div style={{ width: `${pInfo}%`, backgroundColor: '#059669' }} title={`Info Only: ${pInfo}%`} />
                    <div style={{ width: `${pInc}%`, backgroundColor: '#f59e0b' }} title={`Occurrence (CAPA): ${pInc}%`} />
                    <div style={{ width: `${pInv}%`, backgroundColor: '#7e22ce' }} title={`Investigation: ${pInv}%`} />
                    <div style={{ width: `${pWait}%`, backgroundColor: '#ef4444' }} title={`Waiting Assessment: ${pWait}%`} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    <div style={{ padding: '10px 14px', borderRadius: '6px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                      <div style={{ fontSize: '11px', color: '#166534', fontWeight: 600 }}>Info Only ({pInfo}%)</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#065f46' }}>{infoOnlyReports.length} Reports</div>
                      <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '2px' }}>No active CAPA or formal investigation</div>
                    </div>

                    <div style={{ padding: '10px 14px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a' }}>
                      <div style={{ fontSize: '11px', color: '#92400e', fontWeight: 600 }}>Occurrence Reports ({pInc}%)</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#b45309' }}>{incidentReports.length} Reports</div>
                      <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '2px' }}>CAPA issued & assigned to departments</div>
                    </div>

                    <div style={{ padding: '10px 14px', borderRadius: '6px', background: '#faf5ff', border: '1px solid #e9d5ff' }}>
                      <div style={{ fontSize: '11px', color: '#6b21a8', fontWeight: 600 }}>Investigation Reports ({pInv}%)</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#7e22ce' }}>{investigationReports.length} Reports</div>
                      <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '2px' }}>ICAO Annex 13 formal investigations</div>
                    </div>

                    <div style={{ padding: '10px 14px', borderRadius: '6px', background: '#fef2f2', border: '1px solid #fecaca' }}>
                      <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: 600 }}>Waiting Assessment ({pWait}%)</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626' }}>{waitingAssessmentReports.length} Reports</div>
                      <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '2px' }}>Pending SMS triage and classification</div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Severity & Kind Distributions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '16px' }}>
            {/* Severity Distribution */}
            <div className="card" style={{ marginBottom: 0 }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>
                🎯 Initial Severity Distribution
              </h3>
              {(() => {
                const nonDraft = reports.filter((r) => r.status !== 'DRAFT');
                const total = nonDraft.length || 1;
                const crit = nonDraft.filter((r) => r.initial_severity === 'CRITICAL').length;
                const high = nonDraft.filter((r) => r.initial_severity === 'HIGH').length;
                const med = nonDraft.filter((r) => r.initial_severity === 'MEDIUM' || !r.initial_severity).length;
                const low = nonDraft.filter((r) => r.initial_severity === 'LOW').length;

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, marginBottom: '3px' }}>
                        <span style={{ color: '#b91c1c' }}>CRITICAL</span>
                        <span>{crit} ({Math.round((crit / total) * 100)}%)</span>
                      </div>
                      <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(crit / total) * 100}%`, background: '#ef4444' }} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, marginBottom: '3px' }}>
                        <span style={{ color: '#c2410c' }}>HIGH</span>
                        <span>{high} ({Math.round((high / total) * 100)}%)</span>
                      </div>
                      <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(high / total) * 100}%`, background: '#f97316' }} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, marginBottom: '3px' }}>
                        <span style={{ color: '#1d4ed8' }}>MEDIUM</span>
                        <span>{med} ({Math.round((med / total) * 100)}%)</span>
                      </div>
                      <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(med / total) * 100}%`, background: '#3b82f6' }} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, marginBottom: '3px' }}>
                        <span style={{ color: '#475569' }}>LOW</span>
                        <span>{low} ({Math.round((low / total) * 100)}%)</span>
                      </div>
                      <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(low / total) * 100}%`, background: '#94a3b8' }} />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Reporting Domain Breakdown */}
            <div className="card" style={{ marginBottom: 0 }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>
                ✈️ Operational Domain Distribution
              </h3>
              {(() => {
                const nonDraft = reports.filter((r) => r.status !== 'DRAFT');
                const pilotCount = nonDraft.filter((r) => (r.report_type || '').includes('PILOT') || (r.report_type || '').includes('FLIGHT') || (r.report_type || '').includes('ASR')).length;
                const cabinCount = nonDraft.filter((r) => (r.report_type || '').includes('CABIN') || (r.report_type || '').includes('CSR')).length;
                const maintCount = nonDraft.filter((r) => (r.report_type || '').includes('MAINT') || (r.report_type || '').includes('TECH')).length;
                const groundCount = nonDraft.filter((r) => (r.report_type || '').includes('GROUND') || (r.report_type || '').includes('GSR')).length;
                const secCount = nonDraft.filter((r) => (r.report_type || '').includes('SEC')).length;
                const birdCount = nonDraft.filter((r) => (r.report_type || '').includes('BIRD') || (r.report_type || '').includes('WILDLIFE')).length;
                const fatigueCount = nonDraft.filter((r) => (r.report_type || '').includes('FATIGUE') || (r.report_type || '').includes('FRM')).length;

                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Flight Ops (ASR):</span> <strong>{pilotCount}</strong>
                    </div>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Cabin Safety (CSR):</span> <strong>{cabinCount}</strong>
                    </div>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Part-145 Maintenance:</span> <strong>{maintCount}</strong>
                    </div>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Ramp & Ground (GSR):</span> <strong>{groundCount}</strong>
                    </div>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Aviation Security:</span> <strong>{secCount}</strong>
                    </div>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Wildlife / Bird Strike:</span> <strong>{birdCount}</strong>
                    </div>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Crew Fatigue (FRM):</span> <strong>{fatigueCount}</strong>
                    </div>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Other SMS Events:</span> <strong>{nonDraft.length - (pilotCount + cabinCount + maintCount + groundCount + secCount + birdCount + fatigueCount)}</strong>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: Waiting Assesment */}
      {activeTab === 'waiting_assessment' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                ⏳ Reports Awaiting SMS Assessment ({waitingAssessmentReports.length})
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
                All officially submitted reports awaiting triage. Assess report as Info Only, Issue Corrective Action (CAPA), or Start Investigation (Annex 13).
              </p>
            </div>
            <button
              type="button"
              onClick={loadValidationReports}
              className="btn-secondary"
              style={{ width: 'auto', padding: '6px 14px', fontSize: '12px' }}
            >
              🔄 Refresh Desk
            </button>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh field="event_date" sortField={waitingSortField} sortDirection={waitingSortDirection} onSort={requestWaitingSort} style={{ width: '130px' }}>
                    Date Submitted
                  </SortableTh>
                  <SortableTh field="report_number" sortField={waitingSortField} sortDirection={waitingSortDirection} onSort={requestWaitingSort} style={{ width: '140px' }}>
                    Report ID
                  </SortableTh>
                  <SortableTh field="report_category" sortField={waitingSortField} sortDirection={waitingSortDirection} onSort={requestWaitingSort} style={{ width: '150px' }}>
                    Kind of Report
                  </SortableTh>
                  <SortableTh field="title" sortField={waitingSortField} sortDirection={waitingSortDirection} onSort={requestWaitingSort}>
                    Title & Description
                  </SortableTh>
                  <SortableTh field="submitter_name" sortField={waitingSortField} sortDirection={waitingSortDirection} onSort={requestWaitingSort} style={{ width: '160px' }}>
                    Submitter / Dept
                  </SortableTh>
                  <SortableTh field="initial_severity" sortField={waitingSortField} sortDirection={waitingSortDirection} onSort={requestWaitingSort} style={{ width: '110px' }}>
                    Severity
                  </SortableTh>
                  <th style={{ width: '160px', textAlign: 'right' }}>SMS Action</th>
                </tr>
              </thead>
              <tbody>
                {waitingAssessmentReports.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>✓</div>
                      <div style={{ fontWeight: 700, fontSize: '15px', color: '#334155' }}>No Reports Waiting for Assessment</div>
                      <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                        All incoming safety occurrences have been triaged and assessed by SMS.
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedWaitingReports.map((r) => {
                    return (
                      <tr key={r.id}>
                        <td style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                          {r.event_date ? new Date(r.event_date).toLocaleDateString() : (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—')}
                        </td>
                        <td
                          style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb', cursor: 'pointer' }}
                          onClick={() => handleInspectReport(r)}
                          title="Click to inspect report details"
                        >
                          {r.report_number}
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              fontWeight: 600,
                              fontSize: '11px',
                            }}
                          >
                            {r.report_type || 'OCCURRENCE'}
                          </span>
                        </td>
                        <td
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleInspectReport(r)}
                          title="Click to inspect report details"
                        >
                          <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '2px' }}>
                            {r.title}
                          </div>
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#64748b',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '380px',
                            }}
                          >
                            {r.description}
                          </div>
                        </td>
                        <td style={{ fontSize: '12px', color: '#475569' }}>
                          <div>{r.reporter_identity?.full_name || r.created_by || 'Frontline Reporter'}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            {r.assigned_department || r.responsible_department || 'SMS Central'}
                          </div>
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              background:
                                r.initial_severity === 'CRITICAL'
                                  ? '#fee2e2'
                                  : r.initial_severity === 'HIGH'
                                  ? '#ffedd5'
                                  : r.initial_severity === 'MEDIUM'
                                  ? '#eff6ff'
                                  : '#f1f5f9',
                              color:
                                r.initial_severity === 'CRITICAL'
                                  ? '#b91c1c'
                                  : r.initial_severity === 'HIGH'
                                  ? '#c2410c'
                                  : r.initial_severity === 'MEDIUM'
                                  ? '#1d4ed8'
                                  : '#475569',
                            }}
                          >
                            {r.initial_severity || 'MEDIUM'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => handleInspectReport(r)}
                              className="btn-secondary"
                              style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                              title="Inspect details"
                            >
                              Details
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (onNavigateTab) {
                                  onNavigateTab('assess_report', { id: r.id });
                                } else {
                                  const newUrl = `?tab=assess_report&id=${r.id}`;
                                  window.history.pushState({ tab: 'assess_report' }, '', newUrl);
                                  window.dispatchEvent(new PopStateEvent('popstate'));
                                }
                              }}
                              className="btn-primary"
                              style={{
                                width: 'auto',
                                padding: '4px 12px',
                                fontSize: '12px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontWeight: 700,
                              }}
                            >
                              <span>Assess Report →</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={waitingPage}
            totalItems={totalWaitingItems}
            pageSize={waitingPageSize}
            onPageChange={setWaitingPage}
            onPageSizeChange={setWaitingPageSize}
            itemLabel="waiting reports"
          />
        </div>
      )}

      {/* TAB 4: Info Only Reports */}
      {activeTab === 'info_only' && (
        <div>
          {/* Informational Banner */}
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#166534',
              lineHeight: 1.5,
            }}
          >
            <strong>📄 Info Only Archive:</strong> These occurrences were assessed by SMS as informational safety observations. No corrective action plan (CAPA) or formal investigation was required. Records remain archived for safety trend analysis and statistical monitoring.
          </div>

          {/* Filter Bar */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px',
              flexWrap: 'wrap',
              background: '#ffffff',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: '1 1 240px' }}>
              <input
                type="text"
                value={infoFilterSearch}
                onChange={(e) => setInfoFilterSearch(e.target.value)}
                placeholder="Search info only reports..."
                style={{ width: '100%', padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              />
            </div>
            <div>
              <select
                value={infoFilterKind}
                onChange={(e) => setInfoFilterKind(e.target.value)}
                style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              >
                <option value="ALL">All Kinds</option>
                <option value="PILOT">Pilot (ASR)</option>
                <option value="CABIN">Cabin Safety</option>
                <option value="MAINT">Maintenance</option>
                <option value="GROUND">Ground Ops</option>
                <option value="SECURITY">Security</option>
                <option value="FATIGUE">Fatigue</option>
              </select>
            </div>
            {(infoFilterSearch || infoFilterKind !== 'ALL') && (
              <button
                type="button"
                onClick={() => { setInfoFilterSearch(''); setInfoFilterKind('ALL'); }}
                className="btn-secondary"
                style={{ width: 'auto', padding: '4px 10px', fontSize: '11px', color: '#dc2626', borderColor: '#fca5a5' }}
              >
                ✕ Clear
              </button>
            )}
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh field="event_date" sortField={infoOnlySortField} sortDirection={infoOnlySortDirection} onSort={requestInfoOnlySort} style={{ width: '120px' }}>
                    Date
                  </SortableTh>
                  <SortableTh field="report_number" sortField={infoOnlySortField} sortDirection={infoOnlySortDirection} onSort={requestInfoOnlySort} style={{ width: '140px' }}>
                    Report ID
                  </SortableTh>
                  <SortableTh field="report_category" sortField={infoOnlySortField} sortDirection={infoOnlySortDirection} onSort={requestInfoOnlySort} style={{ width: '150px' }}>
                    Kind of Report
                  </SortableTh>
                  <SortableTh field="title" sortField={infoOnlySortField} sortDirection={infoOnlySortDirection} onSort={requestInfoOnlySort}>
                    Title & Description
                  </SortableTh>
                  <SortableTh field="initial_severity" sortField={infoOnlySortField} sortDirection={infoOnlySortDirection} onSort={requestInfoOnlySort} style={{ width: '110px' }}>
                    Severity
                  </SortableTh>
                  <SortableTh field="assessment_notes" sortField={infoOnlySortField} sortDirection={infoOnlySortDirection} onSort={requestInfoOnlySort}>
                    SMS Resolution Notes
                  </SortableTh>
                  <SortableTh field="assessment_outcome" sortField={infoOnlySortField} sortDirection={infoOnlySortDirection} onSort={requestInfoOnlySort} style={{ width: '140px' }}>
                    Outcome
                  </SortableTh>
                  <th style={{ width: '100px', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredInfoOnlyReports.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>📄</div>
                      <div style={{ fontWeight: 600 }}>No Info Only reports found.</div>
                    </td>
                  </tr>
                ) : (
                  paginatedInfoOnlyReports.map((r) => {
                    return (
                      <tr key={r.id}>
                        <td style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                          {r.event_date ? new Date(r.event_date).toLocaleDateString() : (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—')}
                        </td>
                        <td
                          style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb', cursor: 'pointer' }}
                          onClick={() => handleInspectReport(r)}
                        >
                          {r.report_number}
                        </td>
                        <td>
                          <span className="badge-pill" style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '11px', fontWeight: 600 }}>
                            {r.report_type || 'OCCURRENCE'}
                          </span>
                        </td>
                        <td style={{ cursor: 'pointer' }} onClick={() => handleInspectReport(r)}>
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.title}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '340px' }}>
                            {r.description}
                          </div>
                        </td>
                        <td>
                          <span className="badge-pill badge-neutral" style={{ fontSize: '11px', fontWeight: 600 }}>
                            {r.initial_severity || 'LOW'}
                          </span>
                        </td>
                        <td style={{ fontSize: '12px', color: '#475569' }}>
                          {r.assessment_notes || r.validation_notes || 'Filed for informational reference without active CAPA.'}
                        </td>
                        <td>
                          <span className="badge-pill badge-success" style={{ fontSize: '11px', fontWeight: 700 }}>
                            ✓ Info Only (Closed)
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => handleInspectReport(r)}
                            className="btn-secondary"
                            style={{ width: 'auto', padding: '4px 10px', fontSize: '12px' }}
                          >
                            Inspect
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={infoOnlyPage}
            totalItems={totalInfoOnlyItems}
            pageSize={infoOnlyPageSize}
            onPageChange={setInfoOnlyPage}
            onPageSizeChange={setInfoOnlyPageSize}
            itemLabel="info only reports"
          />
        </div>
      )}

      {/* TAB 5: Occurrence Reports */}
      {(activeTab === 'occurrence' || activeTab === 'incident') && (
        <div>
          {/* Informational Banner */}
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#92400e',
              lineHeight: 1.5,
            }}
          >
            <strong>⚡ Occurrence Reports Desk:</strong> Assessed operational occurrences requiring corrective or preventive actions (CAPA). Excludes serious incidents and accidents subject to formal Annex 13 investigations.
          </div>

          {/* Filter Bar */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px',
              flexWrap: 'wrap',
              background: '#ffffff',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: '1 1 240px' }}>
              <input
                type="text"
                value={incidentFilterSearch}
                onChange={(e) => setIncidentFilterSearch(e.target.value)}
                placeholder="Search by action #, report #, title..."
                style={{ width: '100%', padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              />
            </div>
            <div>
              <select
                value={incidentFilterDept}
                onChange={(e) => setIncidentFilterDept(e.target.value)}
                style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              >
                <option value="ALL">All Departments</option>
                <option value="Flight Operations">Flight Operations</option>
                <option value="Technical & Part-145">Technical / Maintenance</option>
                <option value="Ground Handling">Ground Ops & Ramp</option>
                <option value="Cabin Operations">Cabin Operations</option>
                <option value="Security">Aviation Security</option>
                <option value="Quality Assurance">Quality Assurance (QMS)</option>
              </select>
            </div>
            {(incidentFilterSearch || incidentFilterDept !== 'ALL') && (
              <button
                type="button"
                onClick={() => { setIncidentFilterSearch(''); setIncidentFilterDept('ALL'); }}
                className="btn-secondary"
                style={{ width: 'auto', padding: '4px 10px', fontSize: '11px', color: '#dc2626', borderColor: '#fca5a5' }}
              >
                ✕ Clear
              </button>
            )}
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh field="event_date" sortField={incidentSortField} sortDirection={incidentSortDirection} onSort={requestIncidentSort} style={{ width: '120px' }}>
                    Occurrence Date
                  </SortableTh>
                  <SortableTh field="report_number" sortField={incidentSortField} sortDirection={incidentSortDirection} onSort={requestIncidentSort} style={{ width: '130px' }}>
                    Report ID
                  </SortableTh>
                  <SortableTh field="occurrence_number" sortField={incidentSortField} sortDirection={incidentSortDirection} onSort={requestIncidentSort} style={{ width: '160px' }}>
                    Linked Corrective Action
                  </SortableTh>
                  <SortableTh field="report_category" sortField={incidentSortField} sortDirection={incidentSortDirection} onSort={requestIncidentSort} style={{ width: '140px' }}>
                    Kind of Report
                  </SortableTh>
                  <SortableTh field="title" sortField={incidentSortField} sortDirection={incidentSortDirection} onSort={requestIncidentSort}>
                    Title & Description
                  </SortableTh>
                  <SortableTh field="responsible_department" sortField={incidentSortField} sortDirection={incidentSortDirection} onSort={requestIncidentSort} style={{ width: '160px' }}>
                    Responsible Dept
                  </SortableTh>
                  <SortableTh field="corrective_action_due_date" sortField={incidentSortField} sortDirection={incidentSortDirection} onSort={requestIncidentSort} style={{ width: '130px' }}>
                    Due Date
                  </SortableTh>
                  <SortableTh field="initial_severity" sortField={incidentSortField} sortDirection={incidentSortDirection} onSort={requestIncidentSort} style={{ width: '100px' }}>
                    Severity
                  </SortableTh>
                  <th style={{ width: '100px', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredIncidentReports.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>⚡</div>
                      <div style={{ fontWeight: 600 }}>No occurrence reports requiring corrective action.</div>
                    </td>
                  </tr>
                ) : (
                  paginatedIncidentReports.map((r) => {
                    const diffDays = r.action_due_date
                      ? Math.ceil((new Date(r.action_due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                      : null;
                    const isOverdue = diffDays !== null && diffDays < 0;

                    return (
                      <tr key={r.id}>
                        <td style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                          {r.event_date ? new Date(r.event_date).toLocaleDateString() : (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—')}
                        </td>
                        <td
                          style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb', cursor: 'pointer' }}
                          onClick={() => handleInspectReport(r)}
                        >
                          {r.report_number}
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              background: '#fef3c7',
                              color: '#b45309',
                              fontWeight: 700,
                              fontSize: '11px',
                              border: '1px solid #fde68a',
                            }}
                          >
                            ⚡ {r.corrective_action_number || 'CAPA Active'}
                          </span>
                        </td>
                        <td>
                          <span className="badge-pill" style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '11px', fontWeight: 600 }}>
                            {r.report_type || 'OCCURRENCE'}
                          </span>
                        </td>
                        <td style={{ cursor: 'pointer' }} onClick={() => handleInspectReport(r)}>
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.title}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
                            {r.description}
                          </div>
                        </td>
                        <td style={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>
                          {r.responsible_department || r.assigned_department || 'Flight Operations'}
                        </td>
                        <td>
                          {r.action_due_date ? (
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: isOverdue ? '#fee2e2' : '#fef3c7',
                                color: isOverdue ? '#991b1b' : '#92400e',
                              }}
                            >
                              {r.action_due_date.substring(0, 10)} {isOverdue ? '(Overdue)' : ''}
                            </span>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>Standard 30d</span>
                          )}
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              background: r.initial_severity === 'HIGH' ? '#ffedd5' : '#eff6ff',
                              color: r.initial_severity === 'HIGH' ? '#c2410c' : '#1d4ed8',
                            }}
                          >
                            {r.initial_severity || 'MEDIUM'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => handleInspectReport(r)}
                            className="btn-secondary"
                            style={{ width: 'auto', padding: '4px 10px', fontSize: '12px' }}
                          >
                            Inspect
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={incidentPage}
            totalItems={totalIncidentItems}
            pageSize={incidentPageSize}
            onPageChange={setIncidentPage}
            onPageSizeChange={setIncidentPageSize}
            itemLabel="occurrence reports"
          />
        </div>
      )}

      {/* TAB 6: Investigation Reports */}
      {activeTab === 'investigation' && (
        <div>
          {/* Informational Banner */}
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#faf5ff',
              border: '1px solid #e9d5ff',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#6b21a8',
              lineHeight: 1.5,
            }}
          >
            <strong>🔍 Safety Investigation Dossiers (ICAO Annex 13):</strong> Serious incidents and accidents subject to formal safety investigation pursuant to ICAO Annex 13 and EU Regulation 376/2014 standards. Access structured 4-part dossiers (Factual Information, Analysis, Conclusions, Safety Recommendations).
          </div>

          {/* Filter Bar */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px',
              flexWrap: 'wrap',
              background: '#ffffff',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: '1 1 240px' }}>
              <input
                type="text"
                value={investigationFilterSearch}
                onChange={(e) => setInvestigationFilterSearch(e.target.value)}
                placeholder="Search investigation case #, report #, flight..."
                style={{ width: '100%', padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              />
            </div>
            <div>
              <select
                value={investigationFilterSeverity}
                onChange={(e) => setInvestigationFilterSeverity(e.target.value)}
                style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              >
                <option value="ALL">All Severities</option>
                <option value="CRITICAL">CRITICAL (Accidents / Severe Events)</option>
                <option value="HIGH">HIGH (Serious Incidents)</option>
              </select>
            </div>
            {(investigationFilterSearch || investigationFilterSeverity !== 'ALL') && (
              <button
                type="button"
                onClick={() => { setInvestigationFilterSearch(''); setInvestigationFilterSeverity('ALL'); }}
                className="btn-secondary"
                style={{ width: 'auto', padding: '4px 10px', fontSize: '11px', color: '#dc2626', borderColor: '#fca5a5' }}
              >
                ✕ Clear
              </button>
            )}
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh field="event_date" sortField={investigationSortField} sortDirection={investigationSortDirection} onSort={requestInvestigationSort} style={{ width: '120px' }}>
                    Date
                  </SortableTh>
                  <SortableTh field="report_number" sortField={investigationSortField} sortDirection={investigationSortDirection} onSort={requestInvestigationSort} style={{ width: '130px' }}>
                    Report ID
                  </SortableTh>
                  <SortableTh field="investigation_number" sortField={investigationSortField} sortDirection={investigationSortDirection} onSort={requestInvestigationSort} style={{ width: '160px' }}>
                    Investigation Case #
                  </SortableTh>
                  <SortableTh field="report_category" sortField={investigationSortField} sortDirection={investigationSortDirection} onSort={requestInvestigationSort} style={{ width: '140px' }}>
                    Kind of Report
                  </SortableTh>
                  <SortableTh field="title" sortField={investigationSortField} sortDirection={investigationSortDirection} onSort={requestInvestigationSort}>
                    Title & Description
                  </SortableTh>
                  <SortableTh field="initial_severity" sortField={investigationSortField} sortDirection={investigationSortDirection} onSort={requestInvestigationSort} style={{ width: '110px' }}>
                    Severity
                  </SortableTh>
                  <SortableTh field="status" sortField={investigationSortField} sortDirection={investigationSortDirection} onSort={requestInvestigationSort} style={{ width: '140px' }}>
                    Status
                  </SortableTh>
                  <th style={{ width: '220px', textAlign: 'right' }}>Investigation Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvestigationReports.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>🔍</div>
                      <div style={{ fontWeight: 600 }}>No investigation reports found.</div>
                    </td>
                  </tr>
                ) : (
                  paginatedInvestigationReports.map((r) => {
                    const invNumber = r.investigation_number || `INV-${r.report_number}`;
                    return (
                      <tr key={r.id}>
                        <td style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                          {r.event_date ? new Date(r.event_date).toLocaleDateString() : (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—')}
                        </td>
                        <td
                          style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb', cursor: 'pointer' }}
                          onClick={() => handleInspectReport(r)}
                        >
                          {r.report_number}
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              background: '#f3e8ff',
                              color: '#7e22ce',
                              fontWeight: 700,
                              fontSize: '11px',
                              border: '1px solid #d8b4fe',
                            }}
                          >
                            🔍 {invNumber}
                          </span>
                        </td>
                        <td>
                          <span className="badge-pill" style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '11px', fontWeight: 600 }}>
                            {r.report_type || 'OCCURRENCE'}
                          </span>
                        </td>
                        <td style={{ cursor: 'pointer' }} onClick={() => handleInspectReport(r)}>
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.title}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
                            {r.description}
                          </div>
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              background: r.initial_severity === 'CRITICAL' ? '#fee2e2' : '#ffedd5',
                              color: r.initial_severity === 'CRITICAL' ? '#b91c1c' : '#c2410c',
                            }}
                          >
                            {r.initial_severity === 'CRITICAL' ? 'CRITICAL' : 'HIGH'}
                          </span>
                        </td>
                        <td>
                          <span
                            className="badge-pill"
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              background: '#faf5ff',
                              color: '#6b21a8',
                              border: '1px solid #e9d5ff',
                            }}
                          >
                            Investigation Active
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => handleNavigateToInvestigation(r)}
                              style={{
                                width: 'auto',
                                padding: '4px 10px',
                                fontSize: '12px',
                                backgroundColor: '#faf5ff',
                                color: '#7e22ce',
                                border: '1px solid #d8b4fe',
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontWeight: 700,
                                cursor: 'pointer',
                              }}
                              title="Safety Investigations modülünde inceleme dosyasını aç"
                            >
                              <span>🔍 Investigation ↗</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setAnnex13Report(r)}
                              className="btn-primary"
                              style={{
                                width: 'auto',
                                padding: '4px 10px',
                                fontSize: '12px',
                                backgroundColor: '#7e22ce',
                                borderColor: '#6b21a8',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontWeight: 700,
                              }}
                              title="Open official ICAO Annex 13 investigation dossier"
                            >
                              <span>📑 Annex 13 Report</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleInspectReport(r)}
                              className="btn-secondary"
                              style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                            >
                              Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={investigationPage}
            totalItems={totalInvestigationItems}
            pageSize={investigationPageSize}
            onPageChange={setInvestigationPage}
            onPageSizeChange={setInvestigationPageSize}
            itemLabel="investigation reports"
          />
        </div>
      )}

      {/* TAB 7: My Submissions */}
      {activeTab === 'my' && (() => {
        const mySafetyCount = myBaseList.filter((r) => (r.report_category || 'SAFETY') === 'SAFETY').length;
        const myNonSafetyCount = myBaseList.filter((r) => r.report_category === 'NON_SAFETY').length;

        return (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  My Submitted Reports (Safety & Non-Safety)
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
                  Live archive and lifecycle status tracking for all reports submitted by {currentUser?.display_name || 'your profile'}.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => openWizardForCategory('SAFETY')}
                  className="btn-primary"
                  style={{ width: 'auto', padding: '6px 14px', fontSize: '12px' }}
                >
                  + File Safety Report
                </button>
                <button
                  onClick={() => openWizardForCategory('NON_SAFETY')}
                  className="btn-secondary"
                  style={{ width: 'auto', padding: '6px 14px', fontSize: '12px', borderColor: '#6366f1', color: '#4338ca' }}
                >
                  + File Non-Safety Report
                </button>
              </div>
            </div>

            {/* Filter buttons for My Submissions */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setMySubmissionsFilter('ALL')}
                className={mySubmissionsFilter === 'ALL' ? 'btn-primary' : 'btn-secondary'}
                style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
              >
                All Reports ({myBaseList.length})
              </button>
              <button
                onClick={() => setMySubmissionsFilter('SAFETY')}
                className={mySubmissionsFilter === 'SAFETY' ? 'btn-primary' : 'btn-secondary'}
                style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
              >
                Safety Reports ({mySafetyCount})
              </button>
              <button
                onClick={() => setMySubmissionsFilter('NON_SAFETY')}
                className={mySubmissionsFilter === 'NON_SAFETY' ? 'btn-primary' : 'btn-secondary'}
                style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
              >
                Non-Safety Reports ({myNonSafetyCount})
              </button>
            </div>

            {!isUsingUserDirect && nonDraftReports.length > 0 && (
              <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe', marginBottom: '14px', fontSize: '12px', color: '#1e40af' }}>
                ℹ️ <strong>Note:</strong> Showing organizational submissions available for your account inspection. Submit a new report above to file directly under your name.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {paginatedMySubmissions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '36px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                  <span style={{ fontSize: '28px', display: 'block', marginBottom: '8px' }}>👤</span>
                  <h4 style={{ margin: '0 0 4px', fontSize: '15px', color: '#334155' }}>No Reports Found</h4>
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#64748b' }}>
                    Reporting occurrences, hazards, and quality non-conformances is the responsibility of every personnel.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button onClick={() => openWizardForCategory('SAFETY')} className="btn-primary" style={{ width: 'auto', padding: '6px 14px', fontSize: '12px' }}>
                      + File Safety Report
                    </button>
                    <button onClick={() => openWizardForCategory('NON_SAFETY')} className="btn-secondary" style={{ width: 'auto', padding: '6px 14px', fontSize: '12px' }}>
                      + File Non-Safety Report
                    </button>
                  </div>
                </div>
              ) : (
                paginatedMySubmissions.map((r) => {
                  const isNonSafety = r.report_category === 'NON_SAFETY';
                  return (
                    <div
                      key={r.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 18px',
                        background: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        flexWrap: 'wrap',
                        gap: '12px',
                      }}
                    >
                      <div style={{ flex: '1 1 350px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#2563eb', fontWeight: 700 }}>
                            {r.report_number}
                          </span>
                          <span
                            className="badge-pill"
                            style={{
                              fontSize: '11px',
                              background: isNonSafety ? '#e0e7ff' : '#dbeafe',
                              color: isNonSafety ? '#4338ca' : '#1d4ed8',
                              fontWeight: 700,
                            }}
                          >
                            {isNonSafety ? 'NON-SAFETY' : 'SAFETY'} • {r.report_type}
                          </span>
                          <span
                            className="badge-pill"
                            style={{
                              fontSize: '11px',
                              background: r.initial_severity === 'CRITICAL' ? '#fee2e2' : r.initial_severity === 'HIGH' ? '#ffedd5' : '#f1f5f9',
                              color: r.initial_severity === 'CRITICAL' ? '#b91c1c' : r.initial_severity === 'HIGH' ? '#c2410c' : '#475569',
                              fontWeight: 700,
                            }}
                          >
                            {r.initial_severity || 'MEDIUM'}
                          </span>
                        </div>
                        <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', margin: '4px 0' }}>
                          {r.title}
                        </h4>
                        <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 6px 0', lineHeight: 1.4 }}>
                          {r.description?.slice(0, 160)}{r.description && r.description.length > 160 ? '...' : ''}
                        </p>
                        <div style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <span>📅 Event: {r.event_date ? new Date(r.event_date).toLocaleString() : 'N/A'}</span>
                          {r.flight_number && <span>✈️ Flight: {r.flight_number}</span>}
                          {r.airport_code && <span>📍 Airport: {r.airport_code}</span>}
                          {r.assigned_department && (
                            <span style={{ color: '#059669', fontWeight: 600 }}>→ Assigned: {r.assigned_department}</span>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          className={`badge-pill ${
                            r.status === 'CLOSED'
                              ? 'badge-success'
                              : r.status === 'SUBMITTED'
                              ? 'badge-warning'
                              : 'badge-info'
                          }`}
                          style={{ fontSize: '12px', padding: '4px 10px', fontWeight: 700 }}
                        >
                          {r.status === 'SUBMITTED' ? 'Waiting Assessment' : r.status}
                        </span>
                        {r.status === 'SUBMITTED' && (
                          <button
                            type="button"
                            onClick={() => openEditModal(r)}
                            className="btn-secondary"
                            style={{
                              width: 'auto',
                              padding: '6px 12px',
                              fontSize: '12px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontWeight: 600,
                              backgroundColor: '#fffbeb',
                              borderColor: '#fde68a',
                              color: '#b45309',
                            }}
                            title="Edit report before SMS conducts assessment (all changes are logged)"
                          >
                            ✏️ Edit Report
                          </button>
                        )}
                        <button
                          onClick={() => handleInspectReport(r)}
                          className="btn-primary"
                          style={{
                            width: 'auto',
                            padding: '6px 14px',
                            fontSize: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontWeight: 600,
                          }}
                        >
                          View Details & Status
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <TablePagination
              currentPage={mySubmissionsPage}
              totalItems={totalMySubmissionsItems}
              pageSize={mySubmissionsPageSize}
              onPageChange={setMySubmissionsPage}
              onPageSizeChange={setMySubmissionsPageSize}
              itemLabel="submitted reports"
            />
          </div>
        );
      })()}

      {/* TAB 8: Drafts */}
      {activeTab === 'drafts' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                Uncommitted Drafts ({draftReportsList.length})
              </h3>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                Draft reports are locally stored and can be edited or deleted safely before official submission to SMS.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {draftReportsList.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '13px' }}>No active draft reports found.</p>
            ) : (
              draftReportsList.map((r) => (
                <div
                  key={r.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#fffbeb',
                    borderRadius: '8px',
                    border: '1px solid #fde68a',
                  }}
                >
                  <div>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#d97706', fontWeight: 600 }}>
                      {r.report_number} (Draft • {r.report_category || 'SAFETY'} / {r.report_type})
                    </span>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '2px 0' }}>
                      {r.title}
                    </h4>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>{r.description}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleTriageTransition(r.id, 'SUBMITTED')}
                      className="btn-primary"
                      style={{ padding: '6px 14px', fontSize: '12px', width: 'auto' }}
                    >
                      Submit Official
                    </button>
                    <button
                      onClick={() => handleDeleteDraft(r.id)}
                      className="btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '12px', color: '#dc2626', borderColor: '#fca5a5' }}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}



      {/* 4. Modern Unified Submit Report Modal (Same high-fidelity modal as Header Submit Report) */}
      <SubmitReportModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        initialCategory={submitModalCategory}
        initialKindId={submitModalKindId}
        onReportSubmitted={async () => {
          await loadData();
          await loadValidationReports();
        }}
      />

      {/* 5. SMS Triage Acceptance, Classification Override & Department Routing Modal */}
      {triageReport && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <div>
                <h3>SMS Assessment, Classification Override & Assignment</h3>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                  {triageReport.report_number} — Originally filed by reporter as{' '}
                  <strong>
                    {triageReport.original_report_category || triageReport.report_category} /{' '}
                    {triageReport.original_report_type || triageReport.report_type}
                  </strong>
                </p>
              </div>
              <button
                onClick={() => setTriageReport(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSmsTriageSubmit}>
              <div className="modal-body">
                <div className="login-form">
                  <div className="form-field">
                    <label>SMS Verified Classification (Can override reporter selection)</label>
                    <select
                      value={triageCategory}
                      onChange={(e) => {
                        const nextCat = e.target.value as IngestionReportCategory;
                        setTriageCategory(nextCat);
                        setTriageType(nextCat === 'NON_SAFETY' ? 'QUALITY' : 'SAFETY');
                      }}
                    >
                      <option value="SAFETY">SAFETY REPORT (SMS Domain)</option>
                      <option value="NON_SAFETY">NON-SAFETY REPORT (QMS / Compliance / Ops Domain)</option>
                    </select>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-field">
                      <label>Verified Report Type</label>
                      <select
                        value={triageType}
                        onChange={(e) => setTriageType(e.target.value)}
                      >
                        {(triageCategory === 'NON_SAFETY' ? NON_SAFETY_TYPE_OPTIONS : SAFETY_TYPE_OPTIONS).map(
                          (opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          )
                        )}
                      </select>
                    </div>

                    <div className="form-field">
                      <label>Verified Severity</label>
                      <select
                        value={triageSeverity}
                        onChange={(e) => setTriageSeverity(e.target.value)}
                      >
                        <option value="LOW">LOW</option>
                        <option value="MEDIUM">MEDIUM</option>
                        <option value="HIGH">HIGH</option>
                        <option value="CRITICAL">CRITICAL</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-field">
                      <label>Forward / Route to Target Department</label>
                      <select
                        value={triageDepartment}
                        onChange={(e) => setTriageDepartment(e.target.value)}
                      >
                        {TARGET_DEPARTMENTS.map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-field">
                      <label>Target Lifecycle Status</label>
                      <select
                        value={triageStatus}
                        onChange={(e) => setTriageStatus(e.target.value)}
                      >
                        <option value="TRIAGED">VALIDATED (Accepted by SMS)</option>
                        <option value="ASSIGNED">ASSIGNED (Report Assignment)</option>
                        <option value="IN_PROGRESS">IN_PROGRESS (Investigation Active)</option>
                        <option value="PENDING_CLOSURE">PENDING_CLOSURE</option>
                        <option value="CLOSED">CLOSED</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-field">
                    <label>SMS Officer Triage & Reclassification Notes</label>
                    <textarea
                      rows={3}
                      placeholder="Explain classification confirmation or reason for changing Safety <-> Non-Safety, and instructions for the receiving department..."
                      value={triageReason}
                      onChange={(e) => setTriageReason(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setTriageReport(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ width: 'auto' }}
                >
                  Confirm Classification & Route Report
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. Report Full Details & Status Inspection Modal (Shared Component) */}
      <ReportDetailsModal
        isOpen={!!inspectingReport}
        report={inspectingReport}
        onClose={handleCloseInspectReport}
        onReportUpdated={() => {
          loadData();
        }}
      />


      {/* Note: Assess Report modal has been replaced with full-page route at AssessReportPage.tsx */}

      {/* 8. Approve Report Modal (SCRUM-8 Benchmark Dialog) */}
      {approvingReport && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '640px' }}>
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
                  Approve Report #{approvingReport.report_number}
                </h3>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                  Define downstream workflow assignments and mandatory CAA reporting.
                </p>
              </div>
              <button
                onClick={() => setApprovingReport(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '2px' }}>{approvingReport.title}</div>
                <div style={{ color: '#64748b' }}>
                  Area: <strong>{reviewAreaOfOccurrence}</strong> • Type: <strong>{reviewTypeOfOccurrence}</strong> • Classification: <strong>{reviewEventClassification}</strong>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '4px' }}>
                  Define Type of Report
                </label>
                <select
                  value={approveReportType}
                  onChange={(e) => setApproveReportType(e.target.value)}
                  className="form-control"
                  style={{ fontSize: '13px' }}
                >
                  <option value="Occurrence">Occurrence</option>
                  <option value="Hazard">Hazard</option>
                  <option value="Irregularity">Irregularity</option>
                  <option value="Incident">Incident</option>
                  <option value="Near-Miss">Near-Miss</option>
                  <option value="Security Incident">Security Incident</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '4px' }}>
                  Select Type of Risk Assessment
                </label>
                <select
                  value={approveRiskAssessmentType}
                  onChange={(e) => setApproveRiskAssessmentType(e.target.value)}
                  className="form-control"
                  style={{ fontSize: '13px' }}
                >
                  {VALIDATION_RISK_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '4px' }}>
                  Is it mandatory to send the report to the CAA?
                </label>
                <select
                  value={approveMandatoryCaa}
                  onChange={(e) => setApproveMandatoryCaa(e.target.value)}
                  className="form-control"
                  style={{ fontSize: '13px' }}
                >
                  {VALIDATION_CAA_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '4px' }}>
                  Approval Remarks / Sign-off Comments
                </label>
                <textarea
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  rows={3}
                  className="form-control"
                  placeholder="Optional final remarks..."
                  style={{ fontSize: '13px' }}
                />
              </div>
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setApprovingReport(null)}
                className="btn-secondary"
                style={{ width: 'auto', padding: '8px 16px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmApprove}
                disabled={validationSaving}
                className="btn-primary"
                style={{
                  background: '#10b981',
                  borderColor: '#059669',
                  width: 'auto',
                  padding: '8px 20px',
                  fontWeight: 700,
                }}
              >
                {validationSaving ? 'Validating...' : 'Save & Approve Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 9b. Delete Report Confirmation Modal with Mandatory Reason & Warning */}
      {deletingReport && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ borderBottom: '1px solid #fee2e2', background: '#fef2f2' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>⚠️</span> Soft-Delete Report #{deletingReport.report_number}
                </h3>
                <p style={{ fontSize: '12px', color: '#991b1b', margin: '2px 0 0' }}>
                  Aviation Audit Governance: Physical deletion is prohibited; record will be archived.
                </p>
              </div>
              <button
                onClick={() => setDeletingReport(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#991b1b' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <div
                style={{
                  padding: '12px 14px',
                  background: '#fff1f2',
                  border: '1px solid #fecdd3',
                  borderRadius: '6px',
                  marginBottom: '14px',
                  fontSize: '12px',
                  color: '#9f1239',
                  lineHeight: 1.5,
                }}
              >
                <strong>⚠️ Warning:</strong> You are about to remove this report from active intake queues. Under SafetyCore Compliance & Audit rules, this action will be permanently recorded in the immutable audit trail along with your identity and mandatory reason.
              </div>

              <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '6px' }}>
                Reason for Deletion * (Mandatory)
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={4}
                required
                className="form-control"
                placeholder="State why this report is being deleted (e.g. duplicate submission, accidental test record, invalid entry)..."
                style={{ fontSize: '13px' }}
              />
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setDeletingReport(null)}
                className="btn-secondary"
                style={{ width: 'auto', padding: '8px 16px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={validationSaving || deleteReason.trim().length < 3}
                style={{
                  background: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: deleteReason.trim().length < 3 ? 'not-allowed' : 'pointer',
                  opacity: deleteReason.trim().length < 3 ? 0.6 : 1,
                }}
              >
                {validationSaving ? 'Deleting...' : 'Confirm Soft-Delete & Log Audit'}
              </button>
            </div>
          </div>
        </div>
      )}

            {/* 9. Reject Report Confirmation Modal */}
      {rejectingReport && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#b91c1c' }}>
                  Reject Report #{rejectingReport.report_number}
                </h3>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                  State the formal reason for rejection. This will be recorded in the audit trail.
                </p>
              </div>
              <button
                onClick={() => setRejectingReport(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '6px' }}>
                Rejection Reason *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                required
                className="form-control"
                placeholder="State why this report cannot be validated (e.g. invalid submission, duplicate occurrence, false alarm)..."
                style={{ fontSize: '13px' }}
              />
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setRejectingReport(null)}
                className="btn-secondary"
                style={{ width: 'auto', padding: '8px 16px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReject}
                disabled={validationSaving}
                style={{
                  background: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {validationSaving ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Question Editor Modal */}
      {editingQuestion && (
        <QuestionEditorModal
          question={editingQuestion.question}
          formTitle={editingQuestion.formTitle}
          isOpen={Boolean(editingQuestion)}
          onClose={() => setEditingQuestion(null)}
          onSaved={() => {
            loadData();
          }}
        />
      )}
    
      {/* SafetyManager365 Relate Reports Modal */}
      {relateModalReport && (
        <div className="modal-backdrop">
          <div className="modal-container" style={{ maxWidth: '640px' }}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                🔗 Relate Report #{relateModalReport.report_number} to Master Incident
              </h3>
              <button
                onClick={() => setRelateModalReport(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '14px', fontSize: '12px' }}>
                <strong>Current Report:</strong> {relateModalReport.title} ({relateModalReport.report_type}) • {relateModalReport.event_date.substring(0, 10)}
              </div>

              <div className="form-field">
                <label style={{ fontWeight: 700 }}>1. Suggested Matching Reports (4-Criteria Auto Match)</label>
                {suggestedMatches.length === 0 ? (
                  <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic', padding: '8px' }}>
                    No automated matches found for same date / flight / airports. You can select an open report from the list below.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                    {suggestedMatches.map((m) => (
                      <div
                        key={m.id}
                        onClick={() => setSelectedMasterId(m.id)}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: selectedMasterId === m.id ? '2px solid #2563eb' : '1px solid #cbd5e1',
                          background: selectedMasterId === m.id ? '#eff6ff' : '#ffffff',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>
                          #{m.report_number} • {m.title}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                          Flight: {m.flight_number || 'N/A'} • {m.departure_airport || m.airport_code || ''} ➔ {m.destination_airport || ''} • Date: {m.event_date.substring(0, 10)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-field">
                <label style={{ fontWeight: 700 }}>Or Select Master Report from All Open Reports</label>
                <select
                  value={selectedMasterId}
                  onChange={(e) => setSelectedMasterId(e.target.value)}
                >
                  <option value="">-- Choose Master Incident --</option>
                  {reports
                    .filter((r) => r.id !== relateModalReport.id && !r.master_report_id)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        #{r.report_number} - {r.title} ({r.flight_number || 'No Flight'})
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-field">
                <label style={{ fontWeight: 700 }}>Linking Justification / Comment</label>
                <textarea
                  rows={2}
                  placeholder="e.g. Duplicate report from cockpit crew regarding bird strike on same sector"
                  value={relateComment}
                  onChange={(e) => setRelateComment(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setRelateModalReport(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedMasterId}
                onClick={async () => {
                  try {
                    await ingestionService.relateReports(selectedMasterId, relateModalReport.id, relateComment);
                    setRelateModalReport(null);
                    await loadData();
                  } catch (err: any) {
                    alert('Failed to link reports: ' + err.message);
                  }
                }}
                className="btn-primary"
              >
                Confirm Linking as Related Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SafetyManager365 Unrelate / Detach Modal with Mandatory Justification */}
      {unrelateModalReport && (
        <div className="modal-backdrop">
          <div className="modal-container" style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#dc2626' }}>
                Detaching Related Report #{unrelateModalReport.report_number}
              </h3>
              <button
                onClick={() => setUnrelateModalReport(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '13px', color: '#475569', marginBottom: '12px' }}>
                SafetyManager365 Business Rule requires an audit explanation when unlinking related occurrences.
              </p>
              <div className="form-field">
                <label style={{ fontWeight: 700 }}>Mandatory Justification Reason *</label>
                <textarea
                  rows={3}
                  required
                  placeholder="e.g. Incorrect match, separating investigations because event occurred at a different station."
                  value={unrelateComment}
                  onChange={(e) => setUnrelateComment(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setUnrelateModalReport(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!unrelateComment.trim()}
                onClick={async () => {
                  try {
                    await ingestionService.unrelateReport(unrelateModalReport.id, unrelateComment.trim());
                    setUnrelateModalReport(null);
                    await loadData();
                  } catch (err: any) {
                    alert('Failed to unlink report: ' + err.message);
                  }
                }}
                className="btn-danger"
                style={{ background: '#dc2626', color: '#ffffff' }}
              >
                Confirm Detachment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SafetyManager365 Cost Analyst Modal */}
      {costModalReport && (
        <div className="modal-backdrop">
          <div className="modal-container" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                💰 Incident Cost Analysis — #{costModalReport.report_number}
              </h3>
              <button
                onClick={() => setCostModalReport(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-field">
                  <label>Direct Cost (e.g. Repairs, Fuel)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="15000.00"
                    value={directCost}
                    onChange={(e) => setDirectCost(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Indirect Cost (e.g. Delays, Pax)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="5000.00"
                    value={indirectCost}
                    onChange={(e) => setIndirectCost(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Currency</label>
                <select
                  value={costCurrency}
                  onChange={(e) => setCostCurrency(e.target.value)}
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="TRY">TRY (₺)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
              <div className="form-field">
                <label>Cost Breakdown Notes</label>
                <textarea
                  rows={3}
                  placeholder="Describe material replacements, ground delay penalties, and labor hours..."
                  value={costNotes}
                  onChange={(e) => setCostNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setCostModalReport(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await ingestionService.saveReportCosts(costModalReport.id, {
                      direct_cost: directCost ? parseFloat(directCost) : 0,
                      indirect_cost: indirectCost ? parseFloat(indirectCost) : 0,
                      currency: costCurrency,
                      notes: costNotes,
                    });
                    setCostModalReport(null);
                    await loadData();
                  } catch (err: any) {
                    alert('Failed to save costs: ' + err.message);
                  }
                }}
                className="btn-primary"
              >
                Save Incident Costs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 9. Reporter Edit Modal (Accessible prior to SMS Assessment) */}
      {editingReport && (
        <div className="modal-backdrop" onClick={() => !isSavingEdit && setEditingReport(null)}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '680px', width: '92%', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
                  ✏️ Edit Report — {editingReport.report_number || editingReport.id}
                </h3>
                <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#64748b' }}>
                  Awaiting SMS Assessment. All modifications will be logged in the permanent audit trail.
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => !isSavingEdit && setEditingReport(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveReportEdit} style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {editSuccessMsg && (
                <div style={{ padding: '10px 14px', borderRadius: '6px', background: '#ecfdf5', color: '#065f46', fontSize: '13px', fontWeight: 600 }}>
                  ✓ {editSuccessMsg}
                </div>
              )}
              {editErrorMsg && (
                <div style={{ padding: '10px 14px', borderRadius: '6px', background: '#fef2f2', color: '#991b1b', fontSize: '13px', fontWeight: 600 }}>
                  ⚠️ {editErrorMsg}
                </div>
              )}

              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                  Subject / Event Title <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-field">
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Occurrence Date & Time</label>
                  <input
                    type="datetime-local"
                    value={editEventDate}
                    onChange={(e) => setEditEventDate(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div className="form-field">
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Location / Airport</label>
                  <input
                    type="text"
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    placeholder="e.g. LTFM (Istanbul Airport)"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-field">
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Flight Number</label>
                  <input
                    type="text"
                    value={editFlightNumber}
                    onChange={(e) => setEditFlightNumber(e.target.value)}
                    placeholder="e.g. TK 1824"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>

                <div className="form-field">
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Aircraft Registration</label>
                  <input
                    type="text"
                    value={editAircraftReg}
                    onChange={(e) => setEditAircraftReg(e.target.value)}
                    placeholder="e.g. TC-JHV"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                </div>
              </div>

              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Initial Severity</label>
                <select
                  value={editSeverity}
                  onChange={(e) => setEditSeverity(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                >
                  <option value="LOW">LOW</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="HIGH">HIGH</option>
                  <option value="CRITICAL">CRITICAL</option>
                </select>
              </div>

              <div className="form-field">
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                  Detailed Narrative / Description <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  rows={6}
                  required
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', lineHeight: 1.4 }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  disabled={isSavingEdit}
                  onClick={() => setEditingReport(null)}
                  className="btn-secondary"
                  style={{ padding: '8px 16px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingEdit}
                  className="btn-primary"
                  style={{ padding: '8px 20px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  {isSavingEdit ? 'Saving & Logging...' : 'Save & Update Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* 10. ICAO Annex 13 Safety Investigation Dossier Modal */}
      <Annex13ReportModal
        report={annex13Report}
        onClose={() => setAnnex13Report(null)}
      />

    </div>

  );
};
